/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { CubismDefaultParameterId } from '@framework/cubismdefaultparameterid';
import { CubismModelSettingJson } from '@framework/cubismmodelsettingjson';
import {
  BreathParameterData,
  CubismBreath
} from '@framework/effect/cubismbreath';
import { CubismEyeBlink } from '@framework/effect/cubismeyeblink';
import { ICubismModelSetting } from '@framework/icubismmodelsetting';
import { CubismIdHandle } from '@framework/id/cubismid';
import { CubismFramework } from '@framework/live2dcubismframework';
import { CubismMatrix44 } from '@framework/math/cubismmatrix44';
import { CubismUserModel } from '@framework/model/cubismusermodel';
import {
  ACubismMotion,
  FinishedMotionCallback
} from '@framework/motion/acubismmotion';
import { CubismMotion } from '@framework/motion/cubismmotion';
import { CubismExpressionMotionManager } from '@framework/motion/cubismexpressionmotionmanager';
import {
  CubismMotionQueueEntryHandle,
  InvalidMotionQueueEntryHandleValue
} from '@framework/motion/cubismmotionqueuemanager';
import { csmMap } from '@framework/type/csmmap';
import { csmRect } from '@framework/type/csmrectf';
import { csmString } from '@framework/type/csmstring';
import { csmVector } from '@framework/type/csmvector';
import {
  CSM_ASSERT,
  CubismLogError,
  CubismLogInfo
} from '@framework/utils/cubismdebug';

import * as LAppDefine from './lappdefine';
import { frameBuffer, LAppDelegate } from './lappdelegate';
import { canvas, gl } from './lappglmanager';
import { LAppPal } from './lapppal';
import { TextureInfo } from './lapptexturemanager';
import { LAppWavFileHandler } from './lappwavfilehandler';
import { CubismMoc } from '@framework/model/cubismmoc';

// Global resolution tier for texture loading
let currentResolutionTier: string = 'high';

/**
 * Set the global resolution tier for all Live2D models
 * @param tier Resolution tier: 'low', 'medium', or 'high'
 */
export function setGlobalResolutionTier(tier: string): void {
  currentResolutionTier = tier;
  console.log(`[LAppModel] Global resolution tier set to: ${tier}`);
}

/**
 * Get the current global resolution tier
 * @returns Current resolution tier
 */
export function getGlobalResolutionTier(): string {
  return currentResolutionTier;
}

/**
 * Apply resolution tier to texture path
 * Converts paths like "model.8192/texture.png" to "model.2048/texture.png" based on tier
 * @param texturePath Original texture path from model3.json
 * @param tier Resolution tier: 'low', 'medium', or 'high'
 * @returns Modified texture path with appropriate resolution folder
 */
function applyResolutionTier(texturePath: string, tier: string): string {
  // Match resolution folder patterns like .8192/, .4096/, .2048/, .1024/, .512/
  const resolutionPattern = /\.(\d{3,4})\//;
  const match = texturePath.match(resolutionPattern);

  if (!match) {
    // No resolution folder found, return as-is
    return texturePath;
  }

  const originalRes = parseInt(match[1]);
  let targetRes: number;

  switch (tier) {
    case 'low':
      // Use 512 or 25% of original, whichever is reasonable
      targetRes = Math.max(512, Math.floor(originalRes * 0.25));
      break;
    case 'medium':
      // Use 1024-2048 or 50% of original
      targetRes = Math.max(1024, Math.floor(originalRes * 0.5));
      break;
    case 'high':
    default:
      // Use original resolution
      return texturePath;
  }

  // Round to nearest power of 2 or common texture size
  const commonSizes = [512, 1024, 2048, 4096, 8192];
  targetRes = commonSizes.reduce((prev, curr) =>
    Math.abs(curr - targetRes) < Math.abs(prev - targetRes) ? curr : prev
  );

  // Replace the resolution in the path
  const modifiedPath = texturePath.replace(resolutionPattern, `.${targetRes}/`);

  console.log(`[LAppModel] Texture path resolution: ${tier} tier -> ${originalRes} => ${targetRes}`);
  console.log(`[LAppModel]   Original: ${texturePath}`);
  console.log(`[LAppModel]   Modified: ${modifiedPath}`);

  return modifiedPath;
}

export enum LoadStep {
  LoadAssets,
  LoadModel,
  WaitLoadModel,
  LoadPriorityExpressions,
  WaitLoadPriorityExpressions,
  LoadExpression,
  WaitLoadExpression,
  LoadPhysics,
  WaitLoadPhysics,
  LoadPose,
  WaitLoadPose,
  SetupEyeBlink,
  SetupBreath,
  LoadUserData,
  WaitLoadUserData,
  SetupEyeBlinkIds,
  SetupLipSyncIds,
  SetupLayout,
  LoadMotion,
  WaitLoadMotion,
  CompleteInitialize,
  CompleteSetupModel,
  LoadTexture,
  WaitLoadTexture,
  CompleteSetup
}

/**
 * チャンネル表情システムのインターフェース
 */
interface ChannelExpressionState {
  expressionName: string;           // 現在再生中の表情名
  motion: ACubismMotion;           // 表情モーション（後方互換、複数の場合は最初のもの）
  managers: CubismExpressionMotionManager[]; // 表情管理マネージャー（各表情に1つ）
  duration: number;                // 持続時間（-1は永続）
  startTime: number;               // 開始時刻
  fadingOut: boolean;              // フェードアウト中かどうか
  fadeOutStartTime: number;        // フェードアウト開始時刻
  fadeOutDuration: number;         // フェードアウト時間
}

interface ExpressionLoadFailure {
  filePath: string;
  reason: string;
}

interface ExpressionLoadSummary {
  expectedNames: string[];
  loadedNames: string[];
  failed: { name: string; filePath: string; reason: string }[];
  pendingNames: string[];
  pendingDetails: { name: string; filePath: string | null }[];
}

interface RequestedExpressionStatus {
  requestedNames: string[];
  loadedNames: string[];
  failed: { name: string; filePath: string | null; reason: string }[];
  pendingNames: string[];
  pendingDetails: { name: string; filePath: string | null }[];
}

/**
 * ユーザーが実際に使用するモデルの実装クラス<br>
 * モデル生成、機能コンポーネント生成、更新処理とレンダリングの呼び出しを行う。
 */
export class LAppModel extends CubismUserModel {
  /**
   * model3.jsonが置かれたディレクトリとファイルパスからモデルを生成する
   * @param dir
   * @param fileName
   */
  public loadAssets(dir: string, fileName: string): void {
    this._modelHomeDir = dir;
    this._expressionCount = 0;
    this.releaseExpressions();
    this._loadedExpressionNames.clear();
    this._failedExpressionNames.clear();
    this._expressionFileMap.clear();
    this._manifestExpressionPaths.clear();
    this._expressionLoadPromises.clear();
    this._priorityExpressionNames.clear();
    this._priorityExpressionsLoaded = false;
    this._devDefaultsApplied = false;
    this._loadStartTime = performance.now();
    this._priorityLoadStartTime = 0;
    this._registerInjectedExpressionPaths();

    // Start fetching the expression manifest early (runs in parallel with model loading)
    this._manifestLoaded = false;
    this._pendingTextureSync = false;
    this._expressionTextures.clear();
    this._expressionChannelMap.clear();
    this._allManifestTextures.clear();
    this._channelManifestTextures.clear();
    this._loadExpressionManifest();

    // Block rendering if ANY pending wardrobe data exists.
    // This prevents the nude base model from ever being drawn.
    const hasPendingExpressions = !!(window as any).__Live2DPendingWardrobeExpressions;
    const hasPendingTextures = !!(window as any).__Live2DPendingWardrobeTextures;
    this._wardrobeRenderBlocked = hasPendingExpressions || hasPendingTextures;
    if (this._wardrobeRenderBlocked) {
      console.log(
        `[LAppModel] Render blocked until wardrobe textures applied ` +
        `(pendingExpressions=${hasPendingExpressions}, pendingTextures=${hasPendingTextures})`
      );
      // Safety timeout: if wardrobe textures aren't applied within 15s, unblock
      // to avoid a permanently invisible model. Better to flash than freeze.
      setTimeout(() => {
        if (this._wardrobeRenderBlocked) {
          console.warn('[LAppModel] Safety timeout: forcibly unblocking wardrobe render after 15s');
          this._wardrobeRenderBlocked = false;
        }
      }, 15000);
    }

    fetch(`${this._modelHomeDir}${fileName}`)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => {
        const setting: ICubismModelSetting = new CubismModelSettingJson(
          arrayBuffer,
          arrayBuffer.byteLength
        );

        // ステートを更新
        this._state = LoadStep.LoadModel;

        // 結果を保存
        this.setupModel(setting);
      })
      .catch(error => {
        // model3.json読み込みでエラーが発生した時点で描画は不可能なので、setupせずエラーをcatchして何もしない
        CubismLogError(`Failed to load file ${this._modelHomeDir}${fileName}`);
      });
  }

  public getExpressionLoadSummary(): ExpressionLoadSummary {
    const expectedNames: string[] = [];
    const baseDir = this._modelHomeDir || '';

    if (this._modelSetting) {
      const count = this._modelSetting.getExpressionCount();
      for (let i = 0; i < count; i++) {
        const name = this._modelSetting.getExpressionName(i);
        const fileName = this._modelSetting.getExpressionFileName(i);
        expectedNames.push(name);

        if (!this._expressionFileMap.has(name)) {
          this._expressionFileMap.set(name, `${baseDir}${fileName}`);
        }
      }
    }

    const loadedNames = Array.from(this._loadedExpressionNames);
    const failed: { name: string; filePath: string; reason: string }[] = [];

    this._failedExpressionNames.forEach((info, name) => {
      failed.push({
        name,
        filePath: info.filePath,
        reason: info.reason
      });
    });

    const pendingNames = expectedNames.filter(
      name =>
        !this._loadedExpressionNames.has(name) &&
        !this._failedExpressionNames.has(name)
    );

    const pendingDetails = pendingNames.map(name => ({
      name,
      filePath: this._expressionFileMap.has(name)
        ? this._expressionFileMap.get(name) || null
        : null
    }));

    return {
      expectedNames,
      loadedNames,
      failed,
      pendingNames,
      pendingDetails
    };
  }

  public getRequestedExpressionStatus(expressionNames: string[]): RequestedExpressionStatus {
    const requestedNames = Array.from(
      new Set(
        expressionNames
          .map(name => (typeof name === 'string' ? name.trim() : ''))
          .filter(name => name.length > 0)
      )
    );

    const loadedNames = requestedNames.filter(name => this._loadedExpressionNames.has(name));
    const failed: { name: string; filePath: string | null; reason: string }[] = [];
    const pendingNames: string[] = [];
    const pendingDetails: { name: string; filePath: string | null }[] = [];

    for (const name of requestedNames) {
      const failure = this._failedExpressionNames.get(name);
      if (failure) {
        failed.push({
          name,
          filePath: failure.filePath ?? this._expressionFileMap.get(name) ?? null,
          reason: failure.reason,
        });
        continue;
      }

      if (this._loadedExpressionNames.has(name)) {
        continue;
      }

      pendingNames.push(name);
      pendingDetails.push({
        name,
        filePath: this._expressionFileMap.get(name) ?? null,
      });
    }

    return {
      requestedNames,
      loadedNames,
      failed,
      pendingNames,
      pendingDetails,
    };
  }

  /**
   * Determine which expressions should be loaded with priority based on pending wardrobe data
   */
  private _determinePriorityExpressions(): void {
    this._priorityExpressionNames.clear();

    // Check if we have pending wardrobe expressions from Flutter
    const pendingWardrobe = (window as any).__Live2DPendingWardrobeExpressions;

    if (!pendingWardrobe || typeof pendingWardrobe !== 'object') {
      console.log('[LAppModel] No pending wardrobe expressions found');
      return;
    }

    // Collect all unique expression names from all channels
    const prioritySet = new Set<string>();

    for (const channel in pendingWardrobe) {
      const expressionList = pendingWardrobe[channel];
      if (Array.isArray(expressionList)) {
        expressionList.forEach(expr => {
          if (typeof expr === 'string' && expr.trim() !== '') {
            prioritySet.add(expr);
          }
        });
      }
    }

    for (const expressionName of prioritySet) {
      if (this._expressionFileMap.has(expressionName)) {
        this._priorityExpressionNames.add(expressionName);
      }
    }

    const unresolvedExpressions = Array.from(prioritySet).filter(
      expressionName => !this._priorityExpressionNames.has(expressionName)
    );

    console.log(
      `[LAppModel] Identified ${this._priorityExpressionNames.size} priority expressions from wardrobe:`,
      Array.from(this._priorityExpressionNames)
    );

    if (unresolvedExpressions.length > 0) {
      console.warn(
        '[LAppModel] Wardrobe expressions still missing from the expression file map:',
        unresolvedExpressions
      );
    }
  }

  private _registerInjectedExpressionPaths(): void {
    const injectedExpressionPaths = (window as any)
      .__Live2DPendingWardrobeExpressionPaths as Record<string, string> | null;

    if (!injectedExpressionPaths || typeof injectedExpressionPaths !== 'object') {
      return;
    }

    let registeredCount = 0;

    for (const [expressionName, expressionPath] of Object.entries(injectedExpressionPaths)) {
      const normalizedName = typeof expressionName === 'string' ? expressionName.trim() : '';
      const normalizedPath = typeof expressionPath === 'string' ? expressionPath.trim() : '';
      if (!normalizedName || !normalizedPath) {
        continue;
      }

      this._expressionFileMap.set(normalizedName, normalizedPath);
      registeredCount++;
    }

    if (registeredCount > 0) {
      console.log(
        `[LAppModel] Registered ${registeredCount} injected wardrobe expression path(s) before model load`
      );
    }
  }

  private _resolveExpressionPath(expressionName: string, fallbackFileName: string): string {
    const existingPath = this._expressionFileMap.get(expressionName);
    if (existingPath && existingPath.trim().length > 0) {
      return existingPath;
    }

    return `${this._modelHomeDir}${fallbackFileName}`;
  }

  private _lookupExpressionPathFromModelSetting(expressionName: string): string | null {
    if (!this._modelSetting) {
      return null;
    }

    const count = this._modelSetting.getExpressionCount();
    for (let i = 0; i < count; i++) {
      const configuredName = this._modelSetting.getExpressionName(i);
      if (configuredName !== expressionName) {
        continue;
      }

      const fileName = this._modelSetting.getExpressionFileName(i);
      const resolvedPath = this._resolveExpressionPath(expressionName, fileName);
      this._expressionFileMap.set(expressionName, resolvedPath);
      return resolvedPath;
    }

    return null;
  }

  private _lookupExpressionPathFromManifest(expressionName: string): string | null {
    const manifestPath = this._manifestExpressionPaths.get(expressionName) ?? null;
    if (manifestPath && manifestPath.trim().length > 0) {
      this._expressionFileMap.set(expressionName, manifestPath);
      return manifestPath;
    }

    return null;
  }

  private _lookupExpressionPathFromInjectedSnapshot(expressionName: string): string | null {
    const snapshot = (window as any)
      .__Live2DPendingWardrobeExpressionPaths as Record<string, string> | null;

    if (!snapshot || typeof snapshot !== 'object') {
      return null;
    }

    const injectedPath = snapshot[expressionName];
    if (!injectedPath || typeof injectedPath !== 'string' || injectedPath.trim().length === 0) {
      return null;
    }

    const normalizedPath = injectedPath.trim();
    this._expressionFileMap.set(expressionName, normalizedPath);
    return normalizedPath;
  }

  private async _waitForManifestReady(timeoutMs: number = 5000): Promise<void> {
    if (this._manifestLoaded) {
      return;
    }

    const start = Date.now();
    while (!this._manifestLoaded && Date.now() - start < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    if (!this._manifestLoaded) {
      console.warn(
        `[LAppModel] _waitForManifestReady timed out after ${Date.now() - start}ms`
      );
    }
  }

  public async ensureExpressionLoaded(expressionName: string): Promise<boolean> {
    const normalizedName = typeof expressionName === 'string' ? expressionName.trim() : '';
    if (normalizedName.length === 0) {
      return false;
    }

    const existingMotion = this._expressions.getValue(normalizedName);
    if (existingMotion != null) {
      this._loadedExpressionNames.add(normalizedName);
      this._failedExpressionNames.delete(normalizedName);
      return true;
    }

    const inFlight = this._expressionLoadPromises.get(normalizedName);
    if (inFlight) {
      return inFlight;
    }

    const loadPromise = (async () => {
      await this._waitForManifestReady();

      let expressionPath = this._expressionFileMap.get(normalizedName) ?? null;
      expressionPath ??= this._lookupExpressionPathFromInjectedSnapshot(normalizedName);
      expressionPath ??= this._lookupExpressionPathFromManifest(normalizedName);
      expressionPath ??= this._lookupExpressionPathFromModelSetting(normalizedName);

      if (!expressionPath || expressionPath.trim().length == 0) {
        console.warn(
          `[LAppModel] ensureExpressionLoaded: no path found for '${normalizedName}'. ` +
          `manifestLoaded=${this._manifestLoaded}, ` +
          `injectedPath=${this._lookupExpressionPathFromInjectedSnapshot(normalizedName) ?? 'null'}, ` +
          `manifestPath=${this._manifestExpressionPaths.get(normalizedName) ?? 'null'}, ` +
          `fileMapPath=${this._expressionFileMap.get(normalizedName) ?? 'null'}`
        );
        return false;
      }

      console.log(`[LAppModel] ensureExpressionLoaded: loading '${normalizedName}' from ${expressionPath}`);

      try {
        const response = await fetch(expressionPath);
        if (!response.ok) {
          const reason = `HTTP ${response.status}${response.statusText ? ` (${response.statusText})` : ''}`;
          this._failedExpressionNames.set(normalizedName, {
            filePath: expressionPath,
            reason,
          });
          console.warn(`[LAppModel] ensureExpressionLoaded: failed '${normalizedName}' from ${expressionPath} (${reason})`);
          return false;
        }

        const arrayBuffer = await response.arrayBuffer();
        if (arrayBuffer.byteLength === 0) {
          this._failedExpressionNames.set(normalizedName, {
            filePath: expressionPath,
            reason: 'Empty expression file',
          });
          return false;
        }

        const previousMotion = this._expressions.getValue(normalizedName);
        if (previousMotion != null) {
          ACubismMotion.delete(previousMotion);
          this._expressions.setValue(normalizedName, null);
        }

        const motion = this.loadExpression(
          arrayBuffer,
          arrayBuffer.byteLength,
          normalizedName,
        );

        this._expressions.setValue(normalizedName, motion);

        if (motion == null) {
          this._failedExpressionNames.set(normalizedName, {
            filePath: expressionPath,
            reason: 'Expression data could not be parsed',
          });
          return false;
        }

        this._loadedExpressionNames.add(normalizedName);
        this._failedExpressionNames.delete(normalizedName);
        this._expressionFileMap.set(normalizedName, expressionPath);
        console.log(`[LAppModel] ensureExpressionLoaded: loaded '${normalizedName}' successfully`);
        return true;
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        this._failedExpressionNames.set(normalizedName, {
          filePath: expressionPath,
          reason,
        });
        console.warn(`[LAppModel] ensureExpressionLoaded: exception loading '${normalizedName}': ${reason}`);
        return false;
      }
    })();

    this._expressionLoadPromises.set(normalizedName, loadPromise);
    return loadPromise.finally(() => {
      if (this._expressionLoadPromises.get(normalizedName) === loadPromise) {
        this._expressionLoadPromises.delete(normalizedName);
      }
    });
  }

  /**
   * Load missing expressions in parallel & apply all wardrobe channels atomically.
   * Called from Dart via window.applyWardrobeBatch to avoid 30+ sequential JS roundtrips.
   */
  public async loadAndApplyWardrobeBatch(
    channels: Record<string, string[]>,
    paths: Record<string, string>
  ): Promise<{ applied: number; failed: string[] }> {
    const batchStart = performance.now();

    // Register provided expression file paths
    for (const [name, path] of Object.entries(paths)) {
      if (name && path && !this._expressionFileMap.has(name)) {
        this._expressionFileMap.set(name, path);
      }
    }

    // Collect all unique expressions across channels
    const allExpressions = new Set<string>();
    for (const exprs of Object.values(channels)) {
      if (!Array.isArray(exprs)) continue;
      for (const expr of exprs) {
        const trimmed = typeof expr === 'string' ? expr.trim() : '';
        if (trimmed) allExpressions.add(trimmed);
      }
    }

    // Load missing expressions in parallel (skip clear/none expressions)
    const clearNames = new Set(['none', '']);
    const loadPromises: Promise<boolean>[] = [];
    for (const expr of allExpressions) {
      if (clearNames.has(expr.toLowerCase())) continue;
      if (this._expressions.getValue(expr) != null) continue; // already loaded
      loadPromises.push(this.ensureExpressionLoaded(expr));
    }

    if (loadPromises.length > 0) {
      console.log(`[loadAndApplyWardrobeBatch] Loading ${loadPromises.length} expression(s)...`);
      await Promise.all(loadPromises);
    }

    // Apply all channels
    let applied = 0;
    const failed: string[] = [];

    for (const [channel, expressions] of Object.entries(channels)) {
      if (!Array.isArray(expressions) || expressions.length === 0) continue;

      // Clear expression
      if (expressions.length === 1) {
        const normalized = expressions[0].trim().toLowerCase();
        if (normalized === '' || normalized === 'none') {
          this.clearChannel(channel);
          applied++;
          continue;
        }
      }

      if (expressions.length === 1) {
        const motion = this._expressions.getValue(expressions[0]);
        if (motion != null) {
          this.playExpressionInstant(channel, expressions[0]);
          applied++;
        } else {
          // Expression failed to load (e.g. no_earring) — not a real error
          failed.push(`${channel}:${expressions[0]}`);
        }
      } else {
        this.playMultipleExpressions(channel, expressions, true);
        applied++;
      }
    }

    this._syncWardrobeTextures();

    const elapsed = Math.round(performance.now() - batchStart);
    console.log(
      `[loadAndApplyWardrobeBatch] Done in ${elapsed}ms: ${applied} applied, ${failed.length} skipped (${failed.join(', ')})`
    );
    return { applied, failed };
  }

  private _logExpressionLookupFailure(context: string, channel: string, expressionName: string): void {
    const resolvedPath = this._expressionFileMap.get(expressionName) ?? null;
    const failure = this._failedExpressionNames.get(expressionName) ?? null;
    const loaded = this._loadedExpressionNames.has(expressionName);
    const availableExpressions: string[] = [];

    for (let i = 0; i < this._expressions.getSize(); i++) {
      availableExpressions.push(this._expressions._keyValues[i].first);
    }

    console.warn(
      `[${context}] Motion missing for expression '${expressionName}' on channel '${channel}'. ` +
      `resolvedPath=${resolvedPath ?? 'null'}, loaded=${loaded}, failed=${failure != null}`
    );

    if (failure != null) {
      console.warn(
        `[${context}] Last load failure for '${expressionName}': ${failure.reason} (${failure.filePath})`
      );
    }

    console.warn(
      `[${context}] Available expressions (${availableExpressions.length}): ${availableExpressions.join(', ')}`
    );
  }

  /**
   * model3.jsonからモデルを生成する。
   * model3.jsonの記述に従ってモデル生成、モーション、物理演算などのコンポーネント生成を行う。
   *
   * @param setting ICubismModelSettingのインスタンス
   */
  private setupModel(setting: ICubismModelSetting): void {
    this._updating = true;
    this._initialized = false;

    this._modelSetting = setting;

    // CubismModel
    if (this._modelSetting.getModelFileName() != '') {
      const modelFileName = this._modelSetting.getModelFileName();

      fetch(`${this._modelHomeDir}${modelFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${modelFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          this.loadModel(arrayBuffer, this._mocConsistency);

          // Pre-populate expression file map for all expressions
          const count = this._modelSetting.getExpressionCount();
          for (let i = 0; i < count; i++) {
            const expressionName = this._modelSetting.getExpressionName(i);
            const expressionFileName = this._modelSetting.getExpressionFileName(i);
            const expressionPath = this._resolveExpressionPath(
              expressionName,
              expressionFileName,
            );
            this._expressionFileMap.set(expressionName, expressionPath);
          }

          // Check if we have priority expressions to load first
          this._determinePriorityExpressions();

          // Use priority loading path when wardrobe data is present (even with
          // 0 priority expressions) so remaining expressions load in background
          // without blocking the pipeline.  earlyPreloadModel sets
          // __Live2DPendingWardrobeExpressions = {} to opt into this path.
          const hasWardrobeData = !!(window as any).__Live2DPendingWardrobeExpressions &&
            typeof (window as any).__Live2DPendingWardrobeExpressions === 'object';

          if (hasWardrobeData || this._priorityExpressionNames.size > 0) {
            this._state = LoadStep.LoadPriorityExpressions;
            loadPriorityExpressions();
          } else {
            // No wardrobe data at all — load all expressions normally (legacy)
            this._state = LoadStep.LoadExpression;
            loadCubismExpression();
          }
        });

      this._state = LoadStep.WaitLoadModel;
    } else {
      LAppPal.printMessage('Model data does not exist.');
    }

    // Priority Expressions (Wardrobe expressions to load first)
    const loadPriorityExpressions = (): void => {
      if (this._priorityExpressionNames.size === 0) {
        const elapsed = Math.round(performance.now() - this._loadStartTime);
        console.log(`[LAppModel] No priority expressions to load, proceeding to physics (+${elapsed}ms)`);
        this._priorityExpressionsLoaded = true;
        this._state = LoadStep.LoadPhysics;
        loadCubismPhysics();
        return;
      }

      this._priorityLoadStartTime = performance.now();
      const elapsed = Math.round(this._priorityLoadStartTime - this._loadStartTime);
      console.log(`[LAppModel] Loading ${this._priorityExpressionNames.size} priority expressions first (+${elapsed}ms)`);
      const priorityList = Array.from(this._priorityExpressionNames);
      let priorityLoadedCount = 0;

      for (const expressionName of priorityList) {
        const expressionPath = this._expressionFileMap.get(expressionName);
        if (!expressionPath) {
          console.warn(`[LAppModel] Priority expression '${expressionName}' not found in expression file map`);
          priorityLoadedCount++;
          continue;
        }

        const finalizePriorityLoad = () => {
          priorityLoadedCount++;

          if (priorityLoadedCount >= priorityList.length) {
            this._priorityExpressionsLoaded = true;
            const totalElapsed = Math.round(performance.now() - this._loadStartTime);
            const priorityDuration = Math.round(performance.now() - this._priorityLoadStartTime);
            console.log(`[LAppModel] Priority expressions loaded (${this._loadedExpressionNames.size}/${priorityList.length} successful) in ${priorityDuration}ms (+${totalElapsed}ms total)`);

            // Move to physics loading to allow model display
            this._state = LoadStep.LoadPhysics;
            loadCubismPhysics();

            // Remaining expressions will be loaded after CompleteSetup
            // (see _onCompleteSetup) to avoid competing with texture
            // fetches on the localhost HTTP server.
          }
        };

        fetch(expressionPath)
          .then(response => {
            if (response && response.ok) {
              return response.arrayBuffer();
            }

            const status = response ? response.status : 'unknown';
            const statusText = response ? response.statusText || '' : '';
            const reason =
              typeof status === 'number'
                ? `HTTP ${status}${statusText ? ` (${statusText})` : ''}`
                : 'Unknown response error';

            console.error(
              `[LAppModel] Priority expression '${expressionName}' failed to load (${reason})`
            );

            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });

            return new ArrayBuffer(0);
          })
          .then(arrayBuffer => {
            try {
              if (!arrayBuffer || arrayBuffer.byteLength === 0) {
                return;
              }

              const motion: ACubismMotion = this.loadExpression(
                arrayBuffer,
                arrayBuffer.byteLength,
                expressionName
              );

              if (this._expressions.getValue(expressionName) != null) {
                ACubismMotion.delete(
                  this._expressions.getValue(expressionName)
                );
                this._expressions.setValue(expressionName, null);
              }

              this._expressions.setValue(expressionName, motion);

              if (motion) {
                this._loadedExpressionNames.add(expressionName);
                this._failedExpressionNames.delete(expressionName);
              } else if (!this._failedExpressionNames.has(expressionName)) {
                this._failedExpressionNames.set(expressionName, {
                  filePath: expressionPath,
                  reason: 'Expression data could not be parsed'
                });
              }
            } finally {
              finalizePriorityLoad();
            }
          })
          .catch(error => {
            const reason =
              error instanceof Error ? error.message : String(error);
            console.error(
              `[LAppModel] Exception loading priority expression '${expressionName}':`,
              error
            );
            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });
            finalizePriorityLoad();
          });
      }

      this._state = LoadStep.WaitLoadPriorityExpressions;
    };

    // Remaining Expressions (Background loading of non-priority expressions)
    const loadRemainingExpressions = (): void => {
      const backgroundStartTime = performance.now();
      const count = this._modelSetting.getExpressionCount();
      const remainingExpressions: string[] = [];

      for (let i = 0; i < count; i++) {
        const expressionName = this._modelSetting.getExpressionName(i);
        if (!this._priorityExpressionNames.has(expressionName) &&
            !this._loadedExpressionNames.has(expressionName)) {
          remainingExpressions.push(expressionName);
        }
      }

      if (remainingExpressions.length === 0) {
        const elapsed = Math.round(backgroundStartTime - this._loadStartTime);
        console.log(`[LAppModel] No remaining expressions to load (+${elapsed}ms)`);
        return;
      }

      const elapsed = Math.round(backgroundStartTime - this._loadStartTime);
      console.log(`[LAppModel] Loading ${remainingExpressions.length} remaining expressions in background (+${elapsed}ms)`);

      for (const expressionName of remainingExpressions) {
        const expressionPath = this._expressionFileMap.get(expressionName);
        if (!expressionPath) {
          console.warn(`[LAppModel] Remaining expression '${expressionName}' not found in file map`);
          continue;
        }

        fetch(expressionPath)
          .then(response => {
            if (response && response.ok) {
              return response.arrayBuffer();
            }

            const status = response ? response.status : 'unknown';
            const statusText = response ? response.statusText || '' : '';
            const reason =
              typeof status === 'number'
                ? `HTTP ${status}${statusText ? ` (${statusText})` : ''}`
                : 'Unknown response error';

            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });

            return new ArrayBuffer(0);
          })
          .then(arrayBuffer => {
            if (!arrayBuffer || arrayBuffer.byteLength === 0) {
              return;
            }

            const motion: ACubismMotion = this.loadExpression(
              arrayBuffer,
              arrayBuffer.byteLength,
              expressionName
            );

            if (this._expressions.getValue(expressionName) != null) {
              ACubismMotion.delete(
                this._expressions.getValue(expressionName)
              );
              this._expressions.setValue(expressionName, null);
            }

            this._expressions.setValue(expressionName, motion);

            if (motion) {
              this._loadedExpressionNames.add(expressionName);
              this._failedExpressionNames.delete(expressionName);
            } else if (!this._failedExpressionNames.has(expressionName)) {
              this._failedExpressionNames.set(expressionName, {
                filePath: expressionPath,
                reason: 'Expression data could not be parsed'
              });
            }
          })
          .catch(error => {
            const reason =
              error instanceof Error ? error.message : String(error);
            this._failedExpressionNames.set(expressionName, {
              filePath: expressionPath,
              reason
            });
          });
      }
    };

    // Expression (Legacy full load - kept for fallback)
    const loadCubismExpression = (): void => {
      if (this._modelSetting.getExpressionCount() > 0) {
        const count: number = this._modelSetting.getExpressionCount();

        for (let i = 0; i < count; i++) {
          const expressionName = this._modelSetting.getExpressionName(i);
          const expressionFileName =
            this._modelSetting.getExpressionFileName(i);

          const expressionPath = this._resolveExpressionPath(
            expressionName,
            expressionFileName,
          );
          this._expressionFileMap.set(expressionName, expressionPath);
          this._loadedExpressionNames.delete(expressionName);
          this._failedExpressionNames.delete(expressionName);

          const finalizeExpressionLoad = () => {
            this._expressionCount++;

            if (this._expressionCount >= count) {
              this._state = LoadStep.LoadPhysics;

              // callback
              loadCubismPhysics();
            }
          };

          fetch(expressionPath)
            .then(response => {
              if (response && response.ok) {
                return response.arrayBuffer();
              }

              const status = response ? response.status : 'unknown';
              const statusText = response ? response.statusText || '' : '';
              const reason =
                typeof status === 'number'
                  ? `HTTP ${status}${statusText ? ` (${statusText})` : ''}`
                  : 'Unknown response error';

              CubismLogError(`Failed to load file ${expressionPath}`);
              console.error(
                `[LAppModel] Expression '${expressionName}' failed to load (${reason}) from ${expressionPath}`
              );

              this._failedExpressionNames.set(expressionName, {
                filePath: expressionPath,
                reason
              });

              return new ArrayBuffer(0);
            })
            .then(arrayBuffer => {
              try {
                if (!arrayBuffer) {
                  return;
                }

                const motion: ACubismMotion = this.loadExpression(
                  arrayBuffer,
                  arrayBuffer.byteLength,
                  expressionName
                );

                if (this._expressions.getValue(expressionName) != null) {
                  ACubismMotion.delete(
                    this._expressions.getValue(expressionName)
                  );
                  this._expressions.setValue(expressionName, null);
                }

                this._expressions.setValue(expressionName, motion);

                if (motion) {
                  this._loadedExpressionNames.add(expressionName);
                  this._failedExpressionNames.delete(expressionName);
                } else if (!this._failedExpressionNames.has(expressionName)) {
                  this._failedExpressionNames.set(expressionName, {
                    filePath: expressionPath,
                    reason: 'Expression data could not be parsed'
                  });
                }
              } finally {
                finalizeExpressionLoad();
              }
            })
            .catch(error => {
              const reason =
                error instanceof Error ? error.message : String(error);
              console.error(
                `[LAppModel] Exception while loading expression '${expressionName}' from ${expressionPath}:`,
                error
              );
              this._failedExpressionNames.set(expressionName, {
                filePath: expressionPath,
                reason
              });
              finalizeExpressionLoad();
            });
        }
        this._state = LoadStep.WaitLoadExpression;
      } else {
        this._state = LoadStep.LoadPhysics;

        // callback
        loadCubismPhysics();
      }
    };

    // Physics
    const loadCubismPhysics = (): void => {
      if (this._modelSetting.getPhysicsFileName() != '') {
        const physicsFileName = this._modelSetting.getPhysicsFileName();

        fetch(`${this._modelHomeDir}${physicsFileName}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${physicsFileName}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadPhysics(arrayBuffer, arrayBuffer.byteLength);

            this._state = LoadStep.LoadPose;

            // callback
            loadCubismPose();
          });
        this._state = LoadStep.WaitLoadPhysics;
      } else {
        this._state = LoadStep.LoadPose;

        // callback
        loadCubismPose();
      }
    };

    // Pose
    const loadCubismPose = (): void => {
      if (this._modelSetting.getPoseFileName() != '') {
        const poseFileName = this._modelSetting.getPoseFileName();

        fetch(`${this._modelHomeDir}${poseFileName}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${poseFileName}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadPose(arrayBuffer, arrayBuffer.byteLength);

            this._state = LoadStep.SetupEyeBlink;

            // callback
            setupEyeBlink();
          });
        this._state = LoadStep.WaitLoadPose;
      } else {
        this._state = LoadStep.SetupEyeBlink;

        // callback
        setupEyeBlink();
      }
    };

    // EyeBlink
    const setupEyeBlink = (): void => {
      if (this._modelSetting.getEyeBlinkParameterCount() > 0) {
        this._eyeBlink = CubismEyeBlink.create(this._modelSetting);
        this._state = LoadStep.SetupBreath;
      }

      // callback
      setupBreath();
    };

    // Breath
    const setupBreath = (): void => {
      this._breath = CubismBreath.create();

      const breathParameters: csmVector<BreathParameterData> = new csmVector();
      breathParameters.pushBack(
        new BreathParameterData(this._idParamAngleX, 0.0, 15.0, 6.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(this._idParamAngleY, 0.0, 8.0, 3.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(this._idParamAngleZ, 0.0, 10.0, 5.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(this._idParamBodyAngleX, 0.0, 4.0, 15.5345, 0.5)
      );
      breathParameters.pushBack(
        new BreathParameterData(
          CubismFramework.getIdManager().getId(
            CubismDefaultParameterId.ParamBreath
          ),
          0.5,
          0.5,
          3.2345,
          1
        )
      );

      this._breath.setParameters(breathParameters);
      this._state = LoadStep.LoadUserData;

      // callback
      loadUserData();
    };

    // UserData
    const loadUserData = (): void => {
      if (this._modelSetting.getUserDataFile() != '') {
        const userDataFile = this._modelSetting.getUserDataFile();

        fetch(`${this._modelHomeDir}${userDataFile}`)
          .then(response => {
            if (response.ok) {
              return response.arrayBuffer();
            } else if (response.status >= 400) {
              CubismLogError(
                `Failed to load file ${this._modelHomeDir}${userDataFile}`
              );
              return new ArrayBuffer(0);
            }
          })
          .then(arrayBuffer => {
            this.loadUserData(arrayBuffer, arrayBuffer.byteLength);

            this._state = LoadStep.SetupEyeBlinkIds;

            // callback
            setupEyeBlinkIds();
          });

        this._state = LoadStep.WaitLoadUserData;
      } else {
        this._state = LoadStep.SetupEyeBlinkIds;

        // callback
        setupEyeBlinkIds();
      }
    };

    // EyeBlinkIds
    const setupEyeBlinkIds = (): void => {
      const eyeBlinkIdCount: number =
        this._modelSetting.getEyeBlinkParameterCount();

      for (let i = 0; i < eyeBlinkIdCount; ++i) {
        this._eyeBlinkIds.pushBack(
          this._modelSetting.getEyeBlinkParameterId(i)
        );
      }

      this._state = LoadStep.SetupLipSyncIds;

      // callback
      setupLipSyncIds();
    };

    // LipSyncIds
    const setupLipSyncIds = (): void => {
      const lipSyncIdCount = this._modelSetting.getLipSyncParameterCount();

      for (let i = 0; i < lipSyncIdCount; ++i) {
        this._lipSyncIds.pushBack(this._modelSetting.getLipSyncParameterId(i));
      }
      this._state = LoadStep.SetupLayout;

      // callback
      setupLayout();
    };

    // Layout
    const setupLayout = (): void => {
      const layout: csmMap<string, number> = new csmMap<string, number>();

      if (this._modelSetting == null || this._modelMatrix == null) {
        CubismLogError('Failed to setupLayout().');
        return;
      }

      this._modelSetting.getLayoutMap(layout);
      this._modelMatrix.setupFromLayout(layout);
      this._captureBaseModelMatrix();
      this.setModelYOffset(LAppDefine.ModelInitialYOffset);
      this._state = LoadStep.LoadMotion;

      // callback
      loadCubismMotion();
    };

    // Motion — Skip pre-loading entirely.
    // Motions are fetched lazily on first use (startMotion handles null).
    // This avoids 41 HTTP roundtrips through localhost that compete with
    // texture loading.  After CompleteSetup, _onCompleteSetup() will kick
    // off background preloading so subsequent playback is instant.
    const loadCubismMotion = (): void => {
      this._model.saveParameters();
      this._allMotionCount = 0;
      this._motionCount = 0;

      // Count motions for preLoadMotionGroup later
      const motionGroupCount: number = this._modelSetting.getMotionGroupCount();
      for (let i = 0; i < motionGroupCount; i++) {
        this._allMotionCount += this._modelSetting.getMotionCount(
          this._modelSetting.getMotionGroupName(i)
        );
      }

      // Proceed directly to textures — no motion fetches on the critical path.
      this._motionManager.stopAllMotions();
      this._updating = false;
      this._initialized = true;

      // If manifest is already loaded, set up textures immediately.
      // Otherwise wait for it so wardrobe texture filtering can run.
      if (this._manifestLoaded) {
        this._state = LoadStep.LoadTexture;
        this.createRenderer(16);
        this.setupTextures();
        this.getRenderer().startUp(gl);
      } else {
        console.log('[LAppModel] Waiting for manifest before loading textures...');
        const waitForManifest = (): void => {
          if (this._manifestLoaded) {
            this._state = LoadStep.LoadTexture;
            this.createRenderer(16);
            this.setupTextures();
            this.getRenderer().startUp(gl);
          } else {
            setTimeout(waitForManifest, 10);
          }
        };
        setTimeout(waitForManifest, 10);
      }
    };
  }

  /**
   * テクスチャユニットにテクスチャをロードする
   */
  private setupTextures(): void {
    // iPhoneでのアルファ品質向上のためTypescriptではpremultipliedAlphaを採用
    const usePremultiply = true;

    // Force the renderer tint to refresh after textures (or renderer) are rebuilt
    this._lightColorDirty = true;
    this._lastRendererColor = null;

    if (this._state == LoadStep.LoadTexture) {
      // テクスチャ読み込み用
      const textureCount: number = this._modelSetting.getTextureCount();

      // Determine which wardrobe textures to skip at startup.
      // Prefer manifest-based computation (pending wardrobe expressions × manifest texture map).
      // Fall back to explicit texture lists from Dart if the manifest isn't loaded yet.
      let requiredSet = new Set<string>();
      let ownedSet = new Set<string>();

      if (this._manifestLoaded && this._allManifestTextures.size > 0) {
        const pendingWardrobe = (window as any).__Live2DPendingWardrobeExpressions as
          Record<string, string[]> | null;
        if (pendingWardrobe && typeof pendingWardrobe === 'object' && Object.keys(pendingWardrobe).length > 0) {
          for (const channel in pendingWardrobe) {
            const exprs = pendingWardrobe[channel];
            if (Array.isArray(exprs)) {
              for (const expr of exprs) {
                const textures = this._expressionTextures.get(expr);
                if (textures) {
                  for (const t of textures) requiredSet.add(t);
                }
              }
            }
            // Only manage textures for channels that are in the wardrobe state
            const channelTextures = this._channelManifestTextures.get(channel);
            if (channelTextures) {
              for (const t of channelTextures) ownedSet.add(t);
            }
          }
          console.log(`[LAppModel] Startup texture filter (manifest): ${requiredSet.size} required, ${ownedSet.size} tracked`);
        } else {
          // Manifest loaded but no pending expressions — fall through to Dart texture data
          const pendingTextures = (window as any).__Live2DPendingWardrobeTextures as
            { required: string[]; owned: string[] } | null;
          if (pendingTextures) {
            requiredSet = new Set(pendingTextures.required);
            ownedSet = new Set(pendingTextures.owned);
            console.log(`[LAppModel] Startup texture filter (manifest+dart fallback): ${requiredSet.size} required, ${ownedSet.size} owned`);
          } else {
            console.log('[LAppModel] No pending wardrobe at startup — loading all textures normally');
          }
        }
      } else {
        // Fallback: explicit texture data sent by Dart (manifest not loaded yet)
        const pendingTextures = (window as any).__Live2DPendingWardrobeTextures as
          { required: string[]; owned: string[] } | null;
        if (pendingTextures) {
          requiredSet = new Set(pendingTextures.required);
          ownedSet = new Set(pendingTextures.owned);
          console.log(`[LAppModel] Startup texture filter (dart): ${requiredSet.size} required, ${ownedSet.size} owned`);
        }
      }

      if (ownedSet.size > 0) {
        this._requiredWardrobeTextures = requiredSet;
        this._wardrobeOwnedTextures = ownedSet;
      }

      for (
        let modelTextureNumber = 0;
        modelTextureNumber < textureCount;
        modelTextureNumber++
      ) {
        // テクスチャ名が空文字だった場合はロード・バインド処理をスキップ
        if (this._modelSetting.getTextureFileName(modelTextureNumber) == '') {
          console.log('getTextureFileName null');
          continue;
        }

        // WebGLのテクスチャユニットにテクスチャをロードする
        let texturePath =
          this._modelSetting.getTextureFileName(modelTextureNumber);

        // Apply resolution tier to texture path
        texturePath = applyResolutionTier(texturePath, currentResolutionTier);

        const fullPath = this._modelHomeDir + texturePath;

        // Extract basename (e.g. "texture_02.png") and register in the index map
        const basename = texturePath.split('/').pop() ?? texturePath;
        this._textureIndexMap.set(basename, { index: modelTextureNumber, fullPath });

        // Skip textures that are wardrobe-owned but not currently required
        const isOwned = ownedSet.size > 0 && ownedSet.has(basename);
        const isRequired = requiredSet.has(basename);
        if (isOwned && !isRequired) {
          console.log(`[LAppModel] Skipping unequipped wardrobe texture at startup: ${basename}`);
          // Bind a transparent placeholder so this slot never renders black
          this.getRenderer().bindTexture(modelTextureNumber, this._getTransparentTexture());
          this._textureCount++;
          if (this._textureCount >= textureCount) {
            this._state = LoadStep.CompleteSetup;
            this._flushPendingWardrobeTextureSync();
            this._onCompleteSetup();
          }
          continue;
        }

        // ロード完了時に呼び出すコールバック関数
        const onLoad = (textureInfo: TextureInfo): void => {
          this.getRenderer().bindTexture(modelTextureNumber, textureInfo.id);

          this._textureCount++;

          if (this._textureCount >= textureCount) {
            // ロード完了
            this._state = LoadStep.CompleteSetup;
            this._flushPendingWardrobeTextureSync();
            this._onCompleteSetup();
          }
        };

        // 読み込み
        LAppDelegate.getInstance()
          .getTextureManager()
          .createTextureFromPngFile(fullPath, usePremultiply, onLoad);
        this.getRenderer().setIsPremultipliedAlpha(usePremultiply);
      }

      this._state = LoadStep.WaitLoadTexture;

      // Safety timeout: if not all texture callbacks fire within 10 seconds,
      // force transition to CompleteSetup so the model doesn't hang forever.
      const expectedCount = textureCount;
      setTimeout(() => {
        if (this._state === LoadStep.WaitLoadTexture && this._textureCount < expectedCount) {
          console.warn(
            `[LAppModel] Texture loading timeout! Only ${this._textureCount}/${expectedCount} loaded. Forcing CompleteSetup.`
          );
          this._state = LoadStep.CompleteSetup;
          this._flushPendingWardrobeTextureSync();
          this._onCompleteSetup();
        }
      }, 10000);
    }
  }

  /**
   * レンダラを再構築する
   */
  public reloadRenderer(): void {
    this.deleteRenderer();
    this.createRenderer(16);
    this.setupTextures();
  }

  private _flushPendingWardrobeTextureSync(): void {
    if (!this._pendingTextureSync) {
      return;
    }
    if (!this._manifestLoaded || this._state !== LoadStep.CompleteSetup) {
      return;
    }

    this._pendingTextureSync = false;
    this._syncWardrobeTextures();
  }

  /**
   * Called once when textures finish loading (CompleteSetup).
   * Kicks off deferred work that was removed from the critical path:
   * - Remaining (non-priority) expressions
   * - Motion preloading
   */
  private _onCompleteSetup(): void {
    const elapsed = Math.round(performance.now() - this._loadStartTime);
    console.log(`[LAppModel] CompleteSetup reached (+${elapsed}ms) — starting deferred loads`);

    // 1) Load remaining expressions in background
    this._loadRemainingExpressionsDeferred();

    // 2) Preload motions in background (staggered to avoid flooding localhost)
    setTimeout(() => {
      this._preLoadAllMotionsDeferred();
    }, 500);
  }

  /**
   * Load any expressions that haven't been loaded yet (non-priority ones).
   */
  private _loadRemainingExpressionsDeferred(): void {
    const count = this._modelSetting?.getExpressionCount() ?? 0;
    const remaining: string[] = [];

    for (let i = 0; i < count; i++) {
      const name = this._modelSetting.getExpressionName(i);
      if (!this._priorityExpressionNames.has(name) &&
          !this._loadedExpressionNames.has(name)) {
        remaining.push(name);
      }
    }

    if (remaining.length === 0) return;

    const elapsed = Math.round(performance.now() - this._loadStartTime);
    console.log(`[LAppModel] Loading ${remaining.length} remaining expressions in background (+${elapsed}ms)`);

    for (const expressionName of remaining) {
      const expressionPath = this._expressionFileMap.get(expressionName);
      if (!expressionPath) continue;

      fetch(expressionPath)
        .then(response => {
          if (response && response.ok) return response.arrayBuffer();
          this._failedExpressionNames.set(expressionName, {
            filePath: expressionPath,
            reason: `HTTP ${response?.status ?? 'unknown'}`
          });
          return new ArrayBuffer(0);
        })
        .then(arrayBuffer => {
          if (!arrayBuffer || arrayBuffer.byteLength === 0) return;

          const motion: ACubismMotion = this.loadExpression(
            arrayBuffer, arrayBuffer.byteLength, expressionName
          );

          if (this._expressions.getValue(expressionName) != null) {
            ACubismMotion.delete(this._expressions.getValue(expressionName));
            this._expressions.setValue(expressionName, null);
          }
          this._expressions.setValue(expressionName, motion);

          if (motion) {
            this._loadedExpressionNames.add(expressionName);
            this._failedExpressionNames.delete(expressionName);
          }
        })
        .catch(() => {});
    }
  }

  /**
   * Preload all motion groups in background. Called after CompleteSetup
   * so motion fetches don't compete with the critical texture pipeline.
   */
  private _preLoadAllMotionsDeferred(): void {
    const motionGroupCount = this._modelSetting?.getMotionGroupCount() ?? 0;
    if (motionGroupCount === 0) return;

    const elapsed = Math.round(performance.now() - this._loadStartTime);
    console.log(`[LAppModel] Pre-loading ${this._allMotionCount} motions in background (+${elapsed}ms)`);

    for (let i = 0; i < motionGroupCount; i++) {
      this.preLoadMotionGroup(this._modelSetting.getMotionGroupName(i));
    }
  }

  /**
   * Returns the set of expression names that have been dynamically loaded
   * (from subfolders via the manifest system). Used by the debug panel.
   */
  public getLoadedExpressionNames(): ReadonlySet<string> {
    return this._loadedExpressionNames;
  }

  /**
   * Returns the current GPU texture slot status for every slot tracked by the
   * wardrobe system.  Used by the admin debug overlay.
   * - owned=false  → base texture, always fully loaded
   * - owned=true + loaded=true  → wardrobe texture currently bound
   * - owned=true + loaded=false → slot holds the transparent placeholder
   */
  public getTextureStatus(): { slot: number; basename: string; owned: boolean; loaded: boolean; manifested: boolean }[] {
    const result: { slot: number; basename: string; owned: boolean; loaded: boolean; manifested: boolean }[] = [];
    for (const [basename, info] of this._textureIndexMap) {
      const owned = this._wardrobeOwnedTextures.has(basename);
      const loaded = !owned || this._requiredWardrobeTextures.has(basename);
      const manifested = this._allManifestTextures.has(basename);
      result.push({ slot: info.index, basename, owned, loaded, manifested });
    }
    result.sort((a, b) => a.slot - b.slot);
    return result;
  }

  /**
   * Check if the model has completed the entire loading pipeline, including textures.
   */
  public isFullyInitialized(): boolean {
    return this._state === LoadStep.CompleteSetup;
  }

  /**
   * Returns true when a wardrobe texture swap is active or queued.
   * Used by the manager to avoid resuming rendering prematurely.
   */
  public get isTextureSwapActive(): boolean {
    return this._textureSwapRunning || !!this._queuedTextureSwap;
  }

  /**
   * Returns true when the wardrobe render block is active (model should not be drawn).
   */
  public get isWardrobeRenderBlocked(): boolean {
    return this._wardrobeRenderBlocked;
  }

  /**
   * Clear the wardrobe render block without waiting for a texture swap.
   * Called by the manager when it determines no wardrobe swap is needed.
   */
  public clearWardrobeRenderBlock(): void {
    if (this._wardrobeRenderBlocked) {
      console.log('[LAppModel] Wardrobe render block cleared (no swap needed)');
      this._wardrobeRenderBlocked = false;
    }
  }

  /**
   * Get the current load state for diagnostics.
   */
  public getLoadState(): LoadStep {
    return this._state;
  }

  /**
   * Update frequency: 1 = every frame, 2 = every other frame, etc.
   * Higher values reduce CPU cost for models that don't need smooth updates
   * (e.g. body models in head+body split).
   */
  public updateEveryNFrames: number = 1;
  private _updateFrameCounter: number = 0;
  public physicsEveryNFrames: number = 1;
  public physicsEnabled: boolean = true;
  private _physicsFrameCounter: number = 0;
  private _pendingPhysicsDeltaSeconds: number = 0;

  /**
   * 更新
   */
  public update(): void {
    if (this._state != LoadStep.CompleteSetup) return;

    // Throttle: skip update on non-active frames but still accumulate delta
    this._updateFrameCounter++;
    if (this.updateEveryNFrames > 1 && (this._updateFrameCounter % this.updateEveryNFrames) !== 0) {
      return;
    }

    const deltaTimeSeconds: number = LAppPal.getDeltaTime();
    // When throttled, multiply delta by the skip count so physics/motion don't fall behind
    const effectiveDelta = deltaTimeSeconds * Math.min(this.updateEveryNFrames, 4);
    this._userTimeSeconds += effectiveDelta;

    this._updateModelXOffset(effectiveDelta);
    this._updateModelYOffset(effectiveDelta);
  this._updateLightColor(effectiveDelta);

    this._dragManager.update(effectiveDelta);
    this._dragX = this._dragManager.getX();
    this._dragY = this._dragManager.getY();

    // モーションによるパラメータ更新の有無
    let motionUpdated = false;

    //--------------------------------------------------------------------------
    this._model.loadParameters(); // 前回セーブされた状態をロード
    if (this._motionManager.isFinished()) {
      // モーションの再生がない場合、待機モーションの中からランダムで再生する
      this.startRandomMotion(
        LAppDefine.MotionGroupIdle,
        LAppDefine.PriorityIdle
      );
    } else {
      motionUpdated = this._motionManager.updateMotion(
        this._model,
        effectiveDelta
      ); // モーションを更新
    }
    this._model.saveParameters(); // 状態を保存
    //--------------------------------------------------------------------------

    // まばたき
    if (!motionUpdated) {
      if (this._eyeBlink != null) {
        // メインモーションの更新がないとき
        this._eyeBlink.updateParameters(this._model, effectiveDelta); // 目パチ
      }
    }

    if (this._expressionManager != null) {
      this._expressionManager.updateMotion(this._model, effectiveDelta); // 表情でパラメータ更新（相対変化）
    }

    // Update channel-based expressions
    this.updateChannelExpressions(effectiveDelta);

    // ドラッグによる変化
    // ドラッグによる顔の向きの調整
    this._model.addParameterValueById(this._idParamAngleX, this._dragX * 30); // -30から30の値を加える
    this._model.addParameterValueById(this._idParamAngleY, this._dragY * 30);
    this._model.addParameterValueById(
      this._idParamAngleZ,
      this._dragX * this._dragY * -30
    );

    // ドラッグによる体の向きの調整
    this._model.addParameterValueById(
      this._idParamBodyAngleX,
      this._dragX * 10
    ); // -10から10の値を加える

    // ドラッグによる目の向きの調整
    this._model.addParameterValueById(this._idParamEyeBallX, this._dragX); // -1から1の値を加える
    this._model.addParameterValueById(this._idParamEyeBallY, this._dragY);

    // 呼吸など
    if (this._breath != null) {
      this._breath.updateParameters(this._model, effectiveDelta);
    }

    // 物理演算の設定
    if (this._physics != null && this.physicsEnabled) {
      this._pendingPhysicsDeltaSeconds += effectiveDelta;
      this._physicsFrameCounter++;

      if (
        this.physicsEveryNFrames <= 1 ||
        this._physicsFrameCounter % this.physicsEveryNFrames === 0
      ) {
        this._physics.evaluate(this._model, this._pendingPhysicsDeltaSeconds);
        this._pendingPhysicsDeltaSeconds = 0;
      }
    }

    // リップシンクの設定
    if (this._isTalking) {
      // 話しアニメーション中は自動口パク制御
      this._talkingTime += effectiveDelta;
      this._vowelChangeTime += effectiveDelta;

      // 母音を定期的に変更
      if (this._vowelChangeTime >= this._vowelDuration) {
        const vowels = ['A', 'E', 'I', 'O', 'U'];
        this._currentVowel = vowels[Math.floor(Math.random() * vowels.length)];
        this._vowelChangeTime = 0.0;
        // ランダムな継続時間 (0.15-0.4秒)
        this._vowelDuration = 0.15 + Math.random() * 0.25;
      }

      // より自然な話し方のために複数の波を組み合わせ
      const baseWave = Math.sin(this._talkingTime * this._talkingSpeed * 2 * Math.PI);
      const secondaryWave = Math.sin(this._talkingTime * this._talkingSpeed * 3.2 * Math.PI) * 0.3;
      const tertiaryWave = Math.sin(this._talkingTime * this._talkingSpeed * 1.7 * Math.PI) * 0.2;

      // 組み合わせた波形
      const combinedWave = baseWave + secondaryWave + tertiaryWave;

      // ランダム要素を追加（より自然な動きのため）
      const randomVariation = (Math.random() - 0.5) * 0.15;
      const speedVariation = 1.0 + (Math.random() - 0.5) * 0.2;

      // より自然な話し方のために微妙な変化を追加
      const naturalVariation = Math.sin(this._talkingTime * 0.3) * 0.15 + 0.85;

      // 基本的な口の開閉値を計算（より大きな値を生成するように調整）
      let baseIntensity = ((combinedWave * speedVariation + randomVariation) * this._talkingIntensity * naturalVariation);
      // 適切な開閉範囲を確保（0-1の範囲で、しっかり閉じてしっかり開く）
      baseIntensity = Math.max(0.0, Math.min(1.0, (baseIntensity + 1.0) * 0.6));

      // より滑らかな動きのためにイージング適用
      baseIntensity = baseIntensity * baseIntensity * (3.0 - 2.0 * baseIntensity);

      // 現在の母音に基づいて口の形状を調整
      const vowelParams = this.getVowelMouthParameters(this._currentVowel, baseIntensity);


      // 複数の口パラメータに適用
      this.applyMouthParameters(vowelParams);
    } else if (this._isClosingMouth) {
      // 口を徐々に閉じるアニメーション
      this._mouthCloseTime += effectiveDelta;
      const closeDuration = 0.3; // 0.3秒かけて閉じる

      if (this._mouthCloseTime >= closeDuration) {
        // アニメーション完了
        this._isClosingMouth = false;
        this._mouthCloseTime = 0.0;

        // 完全に口を閉じる
        const closedParams = {
          mouthOpenY: 0.0,
          mouthForm: 0.0,
          mouthWidth: 0.0
        };
        this.applyMouthParameters(closedParams);

        // 元のリップシンク状態に戻す
        this._lipsync = this._originalLipSyncState;
        console.log("Mouth closing animation completed, restored lip sync:", this._originalLipSyncState);
      } else {
        // 徐々に口を閉じる
        const progress = this._mouthCloseTime / closeDuration;
        const easeOutProgress = 1.0 - Math.pow(1.0 - progress, 3); // ease-out cubic
        const mouthValue = (1.0 - easeOutProgress) * 0.3; // 現在の口の開き具合から0に向かって減少

        const closingParams = {
          mouthOpenY: mouthValue,
          mouthForm: (1.0 - easeOutProgress) * 0.1, // 形状も徐々にニュートラルに
          mouthWidth: (1.0 - easeOutProgress) * 0.1
        };
        this.applyMouthParameters(closingParams);

        console.log("Closing mouth progress:", (progress * 100).toFixed(1) + "%", "mouth value:", mouthValue.toFixed(3));
      }
    } else if (this._lipsync) {
      let value = 0.0; // リアルタイムでリップシンクを行う場合、システムから音量を取得して、0~1の範囲で値を入力します。

      this._wavFileHandler.update(effectiveDelta);
      value = this._wavFileHandler.getRms();

      for (let i = 0; i < this._lipSyncIds.getSize(); ++i) {
        this._model.addParameterValueById(this._lipSyncIds.at(i), value, 0.8);
      }

      // Ensure mouth form stays at resting value when not talking
      try {
        const mouthFormId = CubismFramework.getIdManager().getId("ParamMouthForm");
        this._model.setParameterValueById(mouthFormId, 0.3);
      } catch (e) {
        // Parameter may not exist on all models
      }
    } else {
      // Idle state: force mouth form to slight smile (0.3) to prevent motion-baked values
      // from making the mouth appear too large
      try {
        const mouthFormId = CubismFramework.getIdManager().getId("ParamMouthForm");
        this._model.setParameterValueById(mouthFormId, 0.3);
      } catch (e) {
        // Parameter may not exist on all models
      }
    }

    // ポーズの設定
    if (this._pose != null) {
      this._pose.updateParameters(this._model, effectiveDelta);
    }

    this._model.update();
  }

  /**
   * 引数で指定したモーションの再生を開始する
   * @param group モーショングループ名
   * @param no グループ内の番号
   * @param priority 優先度
   * @param onFinishedMotionHandler モーション再生終了時に呼び出されるコールバック関数
   * @return 開始したモーションの識別番号を返す。個別のモーションが終了したか否かを判定するisFinished()の引数で使用する。開始できない時は[-1]
   */
  public playMotion(
    group: string,
    no: number,
    priority: number,
    onFinishedMotionHandler?: FinishedMotionCallback
  ): CubismMotionQueueEntryHandle {
    if (priority == LAppDefine.PriorityForce) {
      this._motionManager.setReservePriority(priority);
    } else if (!this._motionManager.reserveMotion(priority)) {
      if (this._debugMode) {
        LAppPal.printMessage("[APP]can't start motion.");
      }
      return InvalidMotionQueueEntryHandleValue;
    }

    const motionFileName = this._modelSetting.getMotionFileName(group, no);

    // ex) idle_0
    const name = `${group}_${no}`;
    let motion: CubismMotion = this._motions.getValue(name) as CubismMotion;
    let autoDelete = false;

    if (motion == null) {
      fetch(`${this._modelHomeDir}${motionFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${motionFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          motion = this.loadMotion(
            arrayBuffer,
            arrayBuffer.byteLength,
            null,
            onFinishedMotionHandler
          );

          if (motion == null) {
            return;
          }

          let fadeTime: number = this._modelSetting.getMotionFadeInTimeValue(
            group,
            no
          );

          if (fadeTime >= 0.0) {
            motion.setFadeInTime(fadeTime);
          }

          fadeTime = this._modelSetting.getMotionFadeOutTimeValue(group, no);
          if (fadeTime >= 0.0) {
            motion.setFadeOutTime(fadeTime);
          }

          motion.setEffectIds(this._eyeBlinkIds, this._lipSyncIds);
          autoDelete = true; // 終了時にメモリから削除
        });
    } else {
      motion.setFinishedMotionHandler(onFinishedMotionHandler);
    }

    //voice
    const voice = this._modelSetting.getMotionSoundFileName(group, no);
    if (voice.localeCompare('') != 0) {
      let path = voice;
      path = this._modelHomeDir + path;
      this._wavFileHandler.start(path);
    }

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]start motion: [${group}_${no}`);
    }
    return this._motionManager.startMotionPriority(
      motion,
      autoDelete,
      priority
    );
  }

  /**
   * ファイル名からモーションのグループとインデックスを検索する
   * @param fileName モーションファイル名（例: "Idle_00.motion3.json" または "Idle_00"）
   * @return {group: string, index: number} | null
   */
  public findMotionByFileName(fileName: string): { group: string; index: number } | null {
    // .motion3.json 拡張子を削除
    const cleanFileName = fileName.replace('.motion3.json', '');

    const motionGroupCount = this._modelSetting.getMotionGroupCount();

    // First, try exact match in model settings
    for (let i = 0; i < motionGroupCount; i++) {
      const groupName = this._modelSetting.getMotionGroupName(i);
      const motionCount = this._modelSetting.getMotionCount(groupName);

      for (let j = 0; j < motionCount; j++) {
        const motionFileName = this._modelSetting.getMotionFileName(groupName, j);
        const cleanMotionFileName = motionFileName.replace('.motion3.json', '');

        if (cleanMotionFileName === cleanFileName || motionFileName === fileName) {
          return { group: groupName, index: j };
        }
      }
    }

    // If not found, try auto-detection by checking if filename contains path parts
    // Check for patterns like "motions/..." in the motion files
    for (let i = 0; i < motionGroupCount; i++) {
      const groupName = this._modelSetting.getMotionGroupName(i);
      const motionCount = this._modelSetting.getMotionCount(groupName);

      for (let j = 0; j < motionCount; j++) {
        const motionFileName = this._modelSetting.getMotionFileName(groupName, j);

        // Extract just the filename without path (e.g., "motions/Idle_00.motion3.json" -> "Idle_00")
        const fileNameOnly = motionFileName.split('/').pop()?.replace('.motion3.json', '') || '';

        if (fileNameOnly === cleanFileName) {
          return { group: groupName, index: j };
        }
      }
    }

    return null;
  }

  /**
   * ランダムに選ばれたモーションの再生を開始する。
   * @param group モーショングループ名
   * @param priority 優先度
   * @param onFinishedMotionHandler モーション再生終了時に呼び出されるコールバック関数
   * @return 開始したモーションの識別番号を返す。個別のモーションが終了したか否かを判定するisFinished()の引数で使用する。開始できない時は[-1]
   */
  public startRandomMotion(
    group: string,
    priority: number,
    onFinishedMotionHandler?: FinishedMotionCallback
  ): CubismMotionQueueEntryHandle {
    if (this._modelSetting.getMotionCount(group) == 0) {
      return InvalidMotionQueueEntryHandleValue;
    }

    const no: number = Math.floor(
      Math.random() * this._modelSetting.getMotionCount(group)
    );

    return this.playMotion(group, no, priority, onFinishedMotionHandler);
  }

  /**
   * 引数で指定した表情モーションをセットする
   *
   * @param expressionId 表情モーションのID
   */
  public setExpression(expressionId: string): void {
    const motion: ACubismMotion = this._expressions.getValue(expressionId);

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]expression: [${expressionId}]`);
    }

    if (motion != null) {
      this._expressionManager.startMotionPriority(
        motion,
        false,
        LAppDefine.PriorityForce
      );
    } else {
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]expression[${expressionId}] is null`);
      }
    }
  }

  /**
   * ランダムに選ばれた表情モーションをセットする
   */
  public setRandomExpression(): void {
    if (this._expressions.getSize() == 0) {
      return;
    }

    const no: number = Math.floor(Math.random() * this._expressions.getSize());

    for (let i = 0; i < this._expressions.getSize(); i++) {
      if (i == no) {
        const name: string = this._expressions._keyValues[i].first;
        this.setExpression(name);
        return;
      }
    }
  }

  /**
   * イベントの発火を受け取る
   */
  public motionEventFired(eventValue: csmString): void {
    CubismLogInfo('{0} is fired on LAppModel!!', eventValue.s);
  }

  /**
   * 当たり判定テスト
   * 指定ＩＤの頂点リストから矩形を計算し、座標をが矩形範囲内か判定する。
   *
   * @param hitArenaName  当たり判定をテストする対象のID
   * @param x             判定を行うX座標
   * @param y             判定を行うY座標
   */
  public hitTest(hitArenaName: string, x: number, y: number): boolean {
    // 透明時は当たり判定無し。
    if (this._opacity < 1) {
      return false;
    }

    const count: number = this._modelSetting.getHitAreasCount();

    for (let i = 0; i < count; i++) {
      if (this._modelSetting.getHitAreaName(i) == hitArenaName) {
        const drawId: CubismIdHandle = this._modelSetting.getHitAreaId(i);
        return this.isHit(drawId, x, y);
      }
    }

    return false;
  }

  /**
   * Get all hit area names defined in the model.
   * @returns Array of hit area name strings
   */
  public getHitAreaNames(): string[] {
    const names: string[] = [];
    if (!this._modelSetting) return names;
    const count = this._modelSetting.getHitAreasCount();
    for (let i = 0; i < count; i++) {
      names.push(this._modelSetting.getHitAreaName(i));
    }
    return names;
  }

  /**
   * モーションデータをグループ名から一括でロードする。
   * モーションデータの名前は内部でModelSettingから取得する。
   *
   * @param group モーションデータのグループ名
   */
  public preLoadMotionGroup(group: string): void {
    for (let i = 0; i < this._modelSetting.getMotionCount(group); i++) {
      const motionFileName = this._modelSetting.getMotionFileName(group, i);

      // ex) idle_0
      const name = `${group}_${i}`;
      if (this._debugMode) {
        LAppPal.printMessage(
          `[APP]load motion: ${motionFileName} => [${name}]`
        );
      }

      fetch(`${this._modelHomeDir}${motionFileName}`)
        .then(response => {
          if (response.ok) {
            return response.arrayBuffer();
          } else if (response.status >= 400) {
            CubismLogError(
              `Failed to load file ${this._modelHomeDir}${motionFileName}`
            );
            return new ArrayBuffer(0);
          }
        })
        .then(arrayBuffer => {
          const tmpMotion: CubismMotion = this.loadMotion(
            arrayBuffer,
            arrayBuffer.byteLength,
            name
          );

          if (tmpMotion != null) {
            let fadeTime = this._modelSetting.getMotionFadeInTimeValue(
              group,
              i
            );
            if (fadeTime >= 0.0) {
              tmpMotion.setFadeInTime(fadeTime);
            }

            fadeTime = this._modelSetting.getMotionFadeOutTimeValue(group, i);
            if (fadeTime >= 0.0) {
              tmpMotion.setFadeOutTime(fadeTime);
            }
            tmpMotion.setEffectIds(this._eyeBlinkIds, this._lipSyncIds);

            if (this._motions.getValue(name) != null) {
              ACubismMotion.delete(this._motions.getValue(name));
            }

            this._motions.setValue(name, tmpMotion);

            this._motionCount++;
            if (this._motionCount >= this._allMotionCount) {
              // All motions loaded — textures were already started in
              // loadCubismMotion(), so nothing to kick off here.
              console.log(`[LAppModel] All ${this._allMotionCount} motions loaded`);
            }
          } else {
            // loadMotionできなかった場合はモーションの総数がずれるので1つ減らす
            this._allMotionCount--;
          }
        });
    }
  }

  /**
   * すべてのモーションデータを解放する。
   */
  public releaseMotions(): void {
    this._motions.clear();
  }

  /**
   * 全ての表情データを解放する。
   */
  public releaseExpressions(): void {
    this._expressions.clear();
  }

  /**
   * モデルを描画する処理。モデルを描画する空間のView-Projection行列を渡す。
   */
  public doDraw(): void {
    if (this._model == null) return;

    // キャンバスサイズを渡す
    const viewport: number[] = [0, 0, canvas.width, canvas.height];

    this.getRenderer().setRenderState(frameBuffer, viewport);
    this._applyLightTintIfNeeded();
    this.getRenderer().drawModel();
  }

  /**
   * Apply the requested light tint to the renderer if it has changed.
   * This is called every frame but short-circuits when the effective color is unchanged.
   */
  private _applyLightTintIfNeeded(): void {
    if (!this._model) {
      return;
    }

    const renderer = this.getRenderer();
    if (!renderer) {
      return;
    }

    if (!this._lightColorDirty && this._lastRendererColor) {
      return;
    }

    const tint = this._computeTintColor();
    const last = this._lastRendererColor;

    if (
      !last ||
      Math.abs(tint.r - last.r) > 0.001 ||
      Math.abs(tint.g - last.g) > 0.001 ||
      Math.abs(tint.b - last.b) > 0.001 ||
      Math.abs(tint.a - last.a) > 0.001
    ) {
      renderer.setModelColor(tint.r, tint.g, tint.b, tint.a);
      this._lastRendererColor = { ...tint };
    }

    this._lightColorDirty = false;
  }

  /**
   * Blend the configured light color with white using the current light intensity.
   */
  private _computeTintColor(): { r: number; g: number; b: number; a: number } {
    const intensity = this._clamp01(this._lightIntensityCurrent);
    const base = this._lightColorCurrent;

    return {
      r: 1 - intensity + intensity * this._clamp01(base.r),
      g: 1 - intensity + intensity * this._clamp01(base.g),
      b: 1 - intensity + intensity * this._clamp01(base.b),
      a: this._clamp01(base.a)
    };
  }

  /**
   * Clamp a value to the [0, 1] range, converting NaN/Infinity to a safe default.
   */
  private _clamp01(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }
    if (value < 0) {
      return 0;
    }
    if (value > 1) {
      return 1;
    }
    return value;
  }

  /**
   * Update the model's light color and intensity. Values greater than 1.0 are interpreted as 0-255 input.
   */
  public setLightColor(
    color: { r: number; g: number; b: number; a?: number },
    intensity: number = 1,
    transitionMs: number = 0
  ): void {
    if (!color) {
      return;
    }

    const safeColor = {
      r: color.r > 1 ? color.r / 255 : color.r,
      g: color.g > 1 ? color.g / 255 : color.g,
      b: color.b > 1 ? color.b / 255 : color.b,
      a: color.a !== undefined ? (color.a > 1 ? color.a / 255 : color.a) : 1
    };

    const targetColor = {
      r: this._clamp01(safeColor.r),
      g: this._clamp01(safeColor.g),
      b: this._clamp01(safeColor.b),
      a: this._clamp01(safeColor.a)
    };
    const targetIntensity = this._clamp01(
      Number.isFinite(intensity) ? intensity : 1
    );

    const safeTransitionMs = Number.isFinite(transitionMs)
      ? Math.max(0, transitionMs)
      : 0;

    this._lightColorTarget = { ...targetColor };
    this._lightIntensityTarget = targetIntensity;
    this._lightLastRequestedMs = safeTransitionMs;

    const durationSeconds = safeTransitionMs / 1000;

    if (!this._lightColorCurrent) {
      this._lightColorCurrent = { ...targetColor };
    }
    if (!this._lightColorStart) {
      this._lightColorStart = { ...this._lightColorCurrent };
    }

    if (durationSeconds <= 0) {
      this._lightColorCurrent = { ...targetColor };
      this._lightIntensityCurrent = targetIntensity;
      this._lightColorStart = { ...this._lightColorCurrent };
      this._lightIntensityStart = this._lightIntensityCurrent;
      this._lightTransitionElapsed = 0;
      this._lightTransitionDuration = 0;
      this._lightAnimating = false;
    } else {
      this._lightColorStart = { ...this._lightColorCurrent };
      this._lightIntensityStart = this._lightIntensityCurrent;
      this._lightTransitionElapsed = 0;
      this._lightTransitionDuration = durationSeconds;
      this._lightAnimating = true;
    }

    this._lightColorDirty = true;
    this._lastRendererColor = null;
  }

  /**
   * Clear any light tint so the renderer uses the neutral color.
   */
  public resetLightColor(transitionMs: number = 0): void {
    this.setLightColor({ r: 1, g: 1, b: 1, a: 1 }, 0, transitionMs);
  }

  /**
   * Retrieve the currently configured light color state for diagnostics.
   */
  public getLightColorState(): {
    color: { r: number; g: number; b: number; a: number };
    intensity: number;
    transitionMs: number;
  } {
    return {
      color: { ...this._lightColorTarget },
      intensity: this._lightIntensityTarget,
      transitionMs: this._lightLastRequestedMs
    };
  }

  /**
   * モデルを描画する処理。モデルを描画する空間のView-Projection行列を渡す。
   */
  public draw(matrix: CubismMatrix44): void {
    if (this._model == null) {
      return;
    }

    // Block drawing entirely until wardrobe textures are confirmed applied.
    // This is set in loadAssets when ANY pending wardrobe data exists,
    // and cleared only when _drainTextureSwapQueue fully completes.
    if (this._wardrobeRenderBlocked) {
      return;
    }

    // 各読み込み終了後
    if (this._state == LoadStep.CompleteSetup) {
      matrix.multiplyByMatrix(this._modelMatrix);

      this.getRenderer().setMvpMatrix(matrix);

      this.doDraw();
    }
  }

  /**
   * Draw only a subset of drawables whose sorted render-order index is in [minOrder, maxOrder).
   * Used for split-model rendering (e.g. hair behind body, face in front).
   * Call update() BEFORE this — update() is run only once per frame.
   */
  public drawWithOrderRange(matrix: CubismMatrix44, minOrder: number, maxOrder: number): void {
    if (this._model == null) return;
    if (this._wardrobeRenderBlocked) return;
    if (this._state != LoadStep.CompleteSetup) return;

    matrix.multiplyByMatrix(this._modelMatrix);
    this.getRenderer().setMvpMatrix(matrix);
    this.getRenderer().setDrawOrderRange(minOrder, maxOrder);
    this.doDraw();
    this.getRenderer().clearDrawOrderRange();
  }

  public async hasMocConsistencyFromFile() {
    CSM_ASSERT(this._modelSetting.getModelFileName().localeCompare(``));

    // CubismModel
    if (this._modelSetting.getModelFileName() != '') {
      const modelFileName = this._modelSetting.getModelFileName();

      const response = await fetch(`${this._modelHomeDir}${modelFileName}`);
      const arrayBuffer = await response.arrayBuffer();

      this._consistency = CubismMoc.hasMocConsistency(arrayBuffer);

      if (!this._consistency) {
        CubismLogInfo('Inconsistent MOC3.');
      } else {
        CubismLogInfo('Consistent MOC3.');
      }

      return this._consistency;
    } else {
      LAppPal.printMessage('Model data does not exist.');
    }
  }

  /**
   * 自動話しアニメーションを開始する
   * @param speed 話す速度（1秒間の口の開閉回数、デフォルト: 4.0）
   * @param intensity 話す強度（口の開閉幅、0.0-1.0、デフォルト: 0.6）
   */
  public startTalking(speed: number = 4.0, intensity: number = 0.6): void {
    console.log("startTalking called with speed:", speed, "intensity:", intensity);

    // Cancel any ongoing mouth closing animation
    if (this._isClosingMouth) {
      console.log("Cancelling mouth closing animation");
      this._isClosingMouth = false;
      this._mouthCloseTime = 0.0;
    }

    if (this._isTalking) {
      console.log("Already talking, ignoring call");
      return; // 既に話している場合は何もしない
    }

    // 現在のリップシンク状態を保存
    this._originalLipSyncState = this._lipsync;
    console.log("Original lip sync state:", this._originalLipSyncState);

    // リップシンクを無効にして、手動制御に切り替え
    this._lipsync = false;

    // 話しアニメーション開始
    this._isTalking = true;
    this._talkingTime = 0.0;
    this._talkingSpeed = Math.max(0.1, speed); // 最小値制限
    this._talkingIntensity = Math.max(0.0, Math.min(1.0, intensity)); // 0.0-1.0にクランプ

    console.log("Talking started - isTalking:", this._isTalking, "speed:", this._talkingSpeed, "intensity:", this._talkingIntensity);
    console.log("Lip sync IDs available:", this._lipSyncIds.getSize());

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]Started talking animation (speed: ${this._talkingSpeed}, intensity: ${this._talkingIntensity})`);
    }
  }

  /**
   * 自動話しアニメーションを停止する
   */
  public endTalking(): void {
    if (!this._isTalking) {
      return; // 話していない場合は何もしない
    }

    // 話しアニメーション停止
    this._isTalking = false;
    this._talkingTime = 0.0;

    // 口を徐々に閉じるアニメーション開始
    this._isClosingMouth = true;
    this._mouthCloseTime = 0.0;

    console.log("Started mouth closing animation");

    if (this._debugMode) {
      LAppPal.printMessage('[APP]Stopping talking animation with smooth mouth close');
    }
  }

  /**
   * 話している状態かどうかを取得する
   * @returns 話している場合はtrue
   */
  public isTalking(): boolean {
    return this._isTalking;
  }

  /**
   * 話しアニメーションの速度を設定する
   * @param speed 新しい速度（1秒間の口の開閉回数）
   */
  public setTalkingSpeed(speed: number): void {
    this._talkingSpeed = Math.max(0.1, speed);
  }

  /**
   * 話しアニメーションの強度を設定する
   * @param intensity 新しい強度（口の開閉幅、0.0-1.0）
   */
  public setTalkingIntensity(intensity: number): void {
    this._talkingIntensity = Math.max(0.0, Math.min(1.0, intensity));
  }

  /**
   * 母音に基づいて口のパラメータを取得する
   * @param vowel 母音 (A, E, I, O, U)
   * @param intensity 基本強度 (0.0-1.0)
   * @returns 口のパラメータオブジェクト
   */
  private getVowelMouthParameters(vowel: string, intensity: number): any {
    // Only apply minimum opening when intensity is above threshold
    const shouldApplyMinimum = intensity > 0.15;
    const minOpening = shouldApplyMinimum ? 0.15 : 0.0; // Smaller minimum, only when actually talking
    const enhancedIntensity = Math.max(minOpening, intensity);

    const params = {
      mouthOpenY: 0.0,
      mouthForm: 0.0,
      mouthWidth: 0.0
    };

    switch (vowel) {
      case 'A':
        // A: 大きく開いた口、やや縦長
        params.mouthOpenY = enhancedIntensity * 0.6 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.6; // 縦長
        params.mouthWidth = 0.2;
        break;
      case 'E':
        // E: 横に広がった口、中程度に開く
        params.mouthOpenY = enhancedIntensity *  0.43 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.3; // 横長
        params.mouthWidth = 0.6;
        break;
      case 'I':
        // I: 小さく開いた口、横に引っ張られた形
        params.mouthOpenY = enhancedIntensity * 0.43 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.35; // 横長
        params.mouthWidth = 0.6;
        break;
      case 'O':
        // O: 丸い形、大きめに開く
        params.mouthOpenY = enhancedIntensity * 0.4 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -1; // やや丸
        params.mouthWidth = -0.2; // 少し窄む
        break;
      case 'U':
        // U: 小さく丸い、但し見えるように開く
        params.mouthOpenY = enhancedIntensity * 0.35 + (shouldApplyMinimum ? 0.05 : 0.0);
        params.mouthForm = -0.9; // 丸い
        params.mouthWidth = -0.6; // 窄む
        break;
      default:
        params.mouthOpenY = enhancedIntensity * 0.8 + (shouldApplyMinimum ? 0.1 : 0.0);
        break;
    }

    // Ensure all values are within valid range, allow closing to 0
    params.mouthOpenY = Math.max(0.0, Math.min(1.0, params.mouthOpenY));
    params.mouthForm = Math.max(-1.0, Math.min(1.0, params.mouthForm));
    params.mouthWidth = Math.max(-1.0, Math.min(1.0, params.mouthWidth));

    return params;
  }

  /**
   * 口のパラメータをモデルに適用する
   * @param params 口のパラメータオブジェクト
   */
  private applyMouthParameters(params: any): void {
    // メインの口の開閉
    if (this._lipSyncIds.getSize() > 0) {
      for (let i = 0; i < this._lipSyncIds.getSize(); ++i) {
        this._model.setParameterValueById(this._lipSyncIds.at(i), params.mouthOpenY);
      }
    } else {
      const mouthParamId = CubismFramework.getIdManager().getId("ParamMouthOpenY");
      this._model.setParameterValueById(mouthParamId, params.mouthOpenY);
    }

    // 追加の口の形状パラメータ（モデルが対応している場合）
    try {
      const mouthFormId = CubismFramework.getIdManager().getId("ParamMouthForm");
      this._model.setParameterValueById(mouthFormId, params.mouthForm);
    } catch (e) {
      // パラメータが存在しない場合は無視
    }

    try {
      const mouthWidthId = CubismFramework.getIdManager().getId("ParamMouthWidth");
      this._model.setParameterValueById(mouthWidthId, params.mouthWidth);
    } catch (e) {
      // パラメータが存在しない場合は無視
    }

    // 他の一般的な口パラメータも試す
    try {
      const mouthSizeId = CubismFramework.getIdManager().getId("ParamMouthSize");
      this._model.addParameterValueById(mouthSizeId, params.mouthWidth, 0.6);
    } catch (e) {
      // パラメータが存在しない場合は無視
    }

  }

  /**
   * チャンネル別表情システムのメソッド群
   */

  /**
   * Apply wardrobe texture changes: loads newly required textures then unloads stale ones.
   * Called via window.setWardrobeTextures() when the user changes their equipped wardrobe.
   * New textures are loaded first to prevent any visible flash before old ones are released.
   */
  /**
   * Queue a wardrobe texture swap. Only ONE swap runs at a time.
   * If a new request arrives while one is in-flight, it replaces any previously
   * queued request (latest-wins). When the current swap finishes, the queued
   * request runs immediately. Rendering is paused for the entire duration and
   * only resumed once the queue is fully drained.
   */
  public applyWardrobeTextures(required: string[], owned: string[]): void {
    const newRequired = new Set(required);
    const newOwned = new Set(owned);

    console.log(
      `[LAppModel] applyWardrobeTextures request: required=${Array.from(newRequired).join(', ') || '(none)'}; ` +
      `owned=${Array.from(newOwned).join(', ') || '(none)'}; ` +
      `previousRequired=${Array.from(this._requiredWardrobeTextures).join(', ') || '(none)'}; ` +
      `previousOwned=${Array.from(this._wardrobeOwnedTextures).join(', ') || '(none)'}`
    );

    this._wardrobeOwnedTextures = newOwned;

    if (newOwned.size === 0) {
      // No wardrobe textures to manage — clear render block if active
      if (this._wardrobeRenderBlocked) {
        console.log('[LAppModel] Wardrobe render block cleared (no owned textures)');
        this._wardrobeRenderBlocked = false;
      }
      return;
    }

    // Stash this request. If a swap is already running, it will pick up
    // whatever is in _queuedTextureSwap when it finishes.
    this._queuedTextureSwap = { required: newRequired, owned: newOwned };

    // If a swap is already in-flight, bump the generation counter so the
    // running swap's .then() detects it is stale and skips the unload step.
    // This prevents a partial required set from blanking textures that a
    // subsequent (correct) swap still needs.
    if (this._textureSwapRunning) {
      ++this._textureSwapGeneration;
      return;
    }

    // Nothing running — kick off the queue processor
    this._drainTextureSwapQueue();
  }

  /**
   * Process queued texture swaps one at a time until the queue is empty.
   * Pauses rendering on the first swap, resumes after the last one finishes.
   */
  private _drainTextureSwapQueue(): void {
    // Take the queued request (there is at most one — latest-wins)
    const swap = this._queuedTextureSwap;
    if (!swap) {
      // Queue empty — we're done, resume rendering
      this._textureSwapRunning = false;
      clearTimeout(this._textureSwapSafetyTimer);
      this._textureSwapSafetyTimer = 0;
      if (this._wardrobeRenderBlocked) {
        console.log('[LAppModel] Wardrobe render block lifted — texture swap queue drained');
        this._wardrobeRenderBlocked = false;
      }
      LAppDelegate.getInstance().resumeRendering();
      return;
    }

    this._queuedTextureSwap = null;
    this._textureSwapRunning = true;

    // Pause rendering on the first swap; reset the safety timer on every swap
    LAppDelegate.getInstance().pauseRendering();
    clearTimeout(this._textureSwapSafetyTimer);
    this._textureSwapSafetyTimer = window.setTimeout(() => {
      if (this._textureSwapRunning) {
        console.warn('[LAppModel] Texture swap safety timeout (8 s) — force-resuming');
        this._textureSwapRunning = false;
        this._queuedTextureSwap = null;
        LAppDelegate.getInstance().resumeRendering();
      }
    }, 8000);

    // Generate a swap-id so that if a NEW swap is queued while we're loading,
    // we can detect that our .then() work is stale and skip the unload step —
    // the next drain iteration will do the correct unload.
    const swapId = ++this._textureSwapGeneration;

    const { required: newRequired, owned: newOwned } = swap;
    const usePremultiply = true;
    const textureManager = LAppDelegate.getInstance().getTextureManager();
    const loadPromises: Promise<void>[] = [];

    for (const [basename, info] of this._textureIndexMap) {
      if (!newOwned.has(basename)) continue;

      if (newRequired.has(basename)) {
        const p = new Promise<void>((resolve) => {
          const timeout = window.setTimeout(() => {
            console.warn(`[LAppModel] Texture load timeout: ${info.fullPath}`);
            resolve();
          }, 4000);
          textureManager.createTextureFromPngFile(info.fullPath, usePremultiply, (textureInfo) => {
            clearTimeout(timeout);
            // Only bind if this swap is still the latest generation
            if (swapId === this._textureSwapGeneration) {
              this.getRenderer().bindTexture(info.index, textureInfo.id);
            }
            resolve();
          });
        });
        loadPromises.push(p);
      }
    }

    Promise.all(loadPromises)
      .then(() => {
        // Only apply unloads if no newer swap has been queued —
        // otherwise the next drain will handle the correct state.
        if (swapId === this._textureSwapGeneration) {
          for (const [basename, info] of this._textureIndexMap) {
            if (!newOwned.has(basename)) continue;
            if (!newRequired.has(basename)) {
              this.getRenderer().bindTexture(info.index, this._getTransparentTexture());
              this._deleteTextureByFilePath(info.fullPath, textureManager);
            }
          }
          this._requiredWardrobeTextures = newRequired;
          console.log(`[LAppModel] Wardrobe textures applied (gen ${swapId})`);
        } else {
          console.log(`[LAppModel] Swap gen ${swapId} superseded by gen ${this._textureSwapGeneration} — skipping unload`);
        }
      })
      .finally(() => {
        // Drain next queued swap, or resume rendering if queue is empty
        this._drainTextureSwapQueue();
      });
  }

  /**
   * Returns (lazily creating) a 1×1 fully-transparent WebGL texture used as a
   * placeholder for wardrobe texture slots that are currently unloaded.
   * Binding this instead of leaving the slot dangling prevents black rendering.
   */
  private _getTransparentTexture(): WebGLTexture {
    if (!this._transparentTexture) {
      const tex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 0])
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.bindTexture(gl.TEXTURE_2D, null);
      this._transparentTexture = tex;
    }
    return this._transparentTexture;
  }

  /** Delete a texture from GPU memory and remove it from the texture manager's list. */
  private _deleteTextureByFilePath(filePath: string, textureManager: any): void {
    const textures = textureManager._textures;
    if (!textures) return;
    for (let i = 0; i < textures.getSize(); i++) {
      const t = textures.at(i);
      if (t && t.fileName === filePath) {
        gl.deleteTexture(t.id);
        textureManager.releaseTextureByTexture(t.id);
        console.log(`[LAppModel] Released wardrobe texture from GPU: ${filePath}`);
        return;
      }
    }
  }

  /**
   * Load the expression manifest from expressions/expression_manifest.json.
   * Called early in loadAssets() to run in parallel with model loading.
   * Populates _expressionTextures, _allManifestTextures, and adds subfolder
   * expressions to _expressionFileMap so they are auto-discovered.
   */
  private _loadExpressionManifest(): void {
    const manifestPath = `${this._modelHomeDir}expressions/expression_manifest.json`;
    fetch(manifestPath)
      .then(response => {
        if (!response.ok) {
          console.warn(
            `[LAppModel] Expression manifest request returned ${response.status} ` +
            `${response.statusText || ''} for ${manifestPath}`
          );
          this._manifestLoaded = true;
          return null;
        }
        return response.json();
      })
      .then(manifest => {
        if (manifest) {
          this._parseExpressionManifest(manifest);
        } else {
          this._manifestLoaded = true;
        }
        if (this._pendingTextureSync) {
          this._pendingTextureSync = false;
          this._syncWardrobeTextures();
        }
      })
      .catch(err => {
        console.warn('[LAppModel] Failed to load expression manifest:', err);
        this._manifestLoaded = true;
      });
  }

  /**
   * Parse expression_manifest.json and populate expression→texture maps.
   * Also registers each subfolder expression in _expressionFileMap so it can
   * be fetched on demand without a model3.json entry.
   */
  private _parseExpressionManifest(manifest: any): void {
    if (!manifest || !Array.isArray(manifest.entries)) {
      this._manifestLoaded = true;
      return;
    }

    for (const entry of manifest.entries) {
      const textures: string[] = Array.isArray(entry.allTextures) ? entry.allTextures : [];
      const expressions: { name: string; file: string; channel?: string }[] = Array.isArray(entry.expressions) ? entry.expressions : [];

      for (const tex of textures) {
        this._allManifestTextures.add(tex);
      }

      for (const expr of expressions) {
        if (!expr.name || !expr.file) continue;

        // Add to file map — subfolder expressions are not in model3.json
        if (!this._expressionFileMap.has(expr.name)) {
          this._expressionFileMap.set(
            expr.name,
            `${this._modelHomeDir}expressions/${expr.file}`
          );
        }

        this._manifestExpressionPaths.set(
          expr.name,
          `${this._modelHomeDir}expressions/${expr.file}`
        );

        // Map expression name → set of required texture basenames
        this._expressionTextures.set(expr.name, new Set(textures));

        // Map expression name → wardrobe channel (from wardrobe config via manifest)
        // Falls back to folderName if no channel is specified in the manifest
        const channel = expr.channel || entry.folderName;
        if (channel) {
          this._expressionChannelMap.set(expr.name, channel);
          // Track which textures belong to which channel
          if (textures.length > 0) {
            let channelTexSet = this._channelManifestTextures.get(channel);
            if (!channelTexSet) {
              channelTexSet = new Set();
              this._channelManifestTextures.set(channel, channelTexSet);
            }
            for (const t of textures) channelTexSet.add(t);
          }
        }
      }
    }

    this._manifestLoaded = true;
    console.log(
      `[LAppModel] Expression manifest: ${manifest.entries.length} folder(s), ` +
      `${this._expressionTextures.size} expression(s), ` +
      `${this._allManifestTextures.size} tracked texture(s)`
    );

    // Kick off background loading for subfolder expressions not yet in _expressions
    const toLoad = Array.from(this._expressionTextures.keys()).filter(
      name => !this._loadedExpressionNames.has(name)
    );
    if (toLoad.length > 0) {
      console.log(`[LAppModel] Loading ${toLoad.length} subfolder expression(s) in background...`);
      this._loadSubfolderExpressions(toLoad);
    }
  }

  /**
   * Background-fetch and register expressions discovered via the manifest.
   * These .exp3.json files live in subfolders and are NOT listed in model3.json,
   * so the normal loading pipeline never picks them up.
   */
  private _loadSubfolderExpressions(names: string[]): void {
    for (const expressionName of names) {
      const expressionPath = this._expressionFileMap.get(expressionName);
      if (!expressionPath) continue;

      fetch(expressionPath)
        .then(response => {
          if (!response.ok) return new ArrayBuffer(0);
          return response.arrayBuffer();
        })
        .then(arrayBuffer => {
          if (!arrayBuffer || arrayBuffer.byteLength === 0) return;

          const motion: ACubismMotion = this.loadExpression(
            arrayBuffer,
            arrayBuffer.byteLength,
            expressionName
          );

          if (this._expressions.getValue(expressionName) != null) {
            ACubismMotion.delete(this._expressions.getValue(expressionName));
            this._expressions.setValue(expressionName, null);
          }
          this._expressions.setValue(expressionName, motion);

          if (motion) {
            this._loadedExpressionNames.add(expressionName);
            this._failedExpressionNames.delete(expressionName);
            console.log(`[LAppModel] Loaded subfolder expression: ${expressionName}`);
          }

          // Once all subfolder expressions are loaded, apply dev defaults if no wardrobe was sent
          if (
            this._loadedExpressionNames.size >= this._expressionFileMap.size &&
            !this._devDefaultsApplied
          ) {
            this._devDefaultsApplied = true;
            const pendingWardrobe = (window as any).__Live2DPendingWardrobeExpressions;
            if (!pendingWardrobe || typeof pendingWardrobe !== 'object' || Object.keys(pendingWardrobe).length === 0) {
              const devDefaults = [
                'no_head_accessory', 'long_hair', 'no_highlight',
                'no_outer_up', 'no_upper', 'no_bottom'
              ].filter(n => this._loadedExpressionNames.has(n));
              if (devDefaults.length > 0) {
                console.log('[LAppModel] No wardrobe data — applying dev default expressions:', devDefaults);
                for (const expr of devDefaults) {
                  const channel = this._expressionChannelMap.get(expr) || `dev_${expr}`;
                  this.playMultipleExpressions(channel, [expr], true);
                }
              }
            }
          }
        })
        .catch(err => {
          const reason = err instanceof Error ? err.message : String(err);
          this._failedExpressionNames.set(expressionName, { filePath: expressionPath, reason });
        });
    }
  }

  /**
   * Sync GPU textures to match the currently active channel expressions.
   * Computes the union of required textures from active expressions (via manifest),
   * then loads newly needed textures and unloads stale ones.
   * Called automatically after any channel expression state change.
   */
  private _syncWardrobeTextures(): void {
    if (!this._manifestLoaded || this._allManifestTextures.size === 0) {
      this._pendingTextureSync = true;
      console.log(
        `[LAppModel] _syncWardrobeTextures deferred: manifestLoaded=${this._manifestLoaded}, ` +
        `trackedManifestTextures=${this._allManifestTextures.size}`
      );
      return;
    }
    if (this._state !== LoadStep.CompleteSetup) {
      this._pendingTextureSync = true;
      console.log(
        `[LAppModel] _syncWardrobeTextures deferred: loadState=${this._state}, ` +
        `channelCount=${this._channelExpressions.getSize()}`
      );
      return;
    }

    // Compute union of required textures from all active (non-fading) channel expressions
    const requiredTextures = new Set<string>();
    const activeChannels: string[] = [];
    for (let i = 0; i < this._channelExpressions.getSize(); i++) {
      const channel = this._channelExpressions._keyValues[i].first;
      const state = this._channelExpressions._keyValues[i].second;
      if (!state || state.fadingOut) continue;

      activeChannels.push(`${channel}:${state.expressionName}`);

      // expressionName may be "a+b+c" for multi-expression channels
      const names = state.expressionName.split('+');
      for (const name of names) {
        const normalizedName = name.trim();
        const textures = this._expressionTextures.get(normalizedName);
        if (textures) {
          for (const t of textures) requiredTextures.add(t);
          console.log(
            `[LAppModel] Texture sync map hit: expression='${normalizedName}' -> ${Array.from(textures).join(', ') || '(none)'}`
          );
        } else {
          console.warn(
            `[LAppModel] Texture sync map miss: expression='${normalizedName}', ` +
            `resolvedPath=${this._expressionFileMap.get(normalizedName) ?? 'null'}, ` +
            `loaded=${this._loadedExpressionNames.has(normalizedName)}, ` +
            `failed=${this._failedExpressionNames.has(normalizedName)}`
          );
        }
      }
    }

    // Use the union of manifest textures and Dart-provided owned textures.
    // Base outfit textures may only be in the Dart config (no manifest folder),
    // so they must stay in the owned set to be properly managed (blanked when
    // switching to a different outfit).
    // IMPORTANT: Only include manifest textures for channels that have an active
    // expression. If no wardrobe item is equipped on a channel, its textures
    // should be left as-is (model default) rather than blanked to transparent.
    const activeManifestTextures = new Set<string>();
    for (let i = 0; i < this._channelExpressions.getSize(); i++) {
      const channel = this._channelExpressions._keyValues[i].first;
      const state = this._channelExpressions._keyValues[i].second;
      if (!state || state.fadingOut) continue;
      const channelTextures = this._channelManifestTextures.get(channel);
      if (channelTextures) {
        for (const t of channelTextures) activeManifestTextures.add(t);
      }
    }
    const effectiveOwned = new Set([...activeManifestTextures, ...this._wardrobeOwnedTextures]);

    console.log(
      `[LAppModel] _syncWardrobeTextures activeChannels=${activeChannels.join(' | ') || '(none)'}; ` +
      `required=${Array.from(requiredTextures).join(', ') || '(none)'}; ` +
      `owned=${Array.from(effectiveOwned).join(', ') || '(none)'}`
    );

    // Only manage textures if wardrobe was set up at startup (owning textures) OR
    // if some active expression actually needs a texture. Skip entirely in dev mode
    // where no wardrobe data was provided — all textures remain loaded as-is.
    if (requiredTextures.size > 0 || this._wardrobeOwnedTextures.size > 0) {
      this.applyWardrobeTextures(
        Array.from(requiredTextures),
        Array.from(effectiveOwned)
      );
    }
  }

  /**
   * チャンネル別表情を再生する
   * @param channel チャンネル名
   * @param expressionName 表情名
   * @param duration 持続時間（秒、-1で永続）
   */
  public playExpression(channel: string, expressionName: string, duration: number): void {
    if (this._debugMode) {
      LAppPal.printMessage(`[APP]playExpression: channel=${channel}, expression=${expressionName}, duration=${duration}`);
    }

    // 表情データの取得
    const motion: ACubismMotion = this._expressions.getValue(expressionName);
    if (motion == null) {
      this._logExpressionLookupFailure('playExpression', channel, expressionName);
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]Expression '${expressionName}' not found`);
      }
      return;
    }

    // Skip if the same expression is already active and not fading out.
    // This prevents the jarring remove-and-readd flicker.
    if (this._channelExpressions.isExist(channel)) {
      const existing = this._channelExpressions.getValue(channel);
      if (existing != null && existing.expressionName === expressionName && !existing.fadingOut) {
        console.log(`Expression '${expressionName}' already active on channel '${channel}', skipping`);
        return;
      }
    }

    // 既存のチャンネル表情がある場合はフェードアウトを開始（即削除しない）
    // Let updateChannelExpressions handle the gradual fade-out and cleanup.
    if (this._channelExpressions.isExist(channel)) {
      const existing = this._channelExpressions.getValue(channel);
      if (existing != null) {
        // Move old expression to a temporary fade-out channel so both can
        // coexist briefly: old one fading out, new one fading in.
        const fadeOutChannel = `__fadeout_${channel}_${Date.now()}`;
        this._eraseChannelExpression(channel);
        this._channelExpressions.setValue(fadeOutChannel, existing);
        existing.fadingOut = true;
        existing.fadeOutStartTime = this._userTimeSeconds;
        existing.fadeOutDuration = this._defaultFadeDuration;
      } else {
        this._eraseChannelExpression(channel);
      }
    }

    // 新しい表情マネージャーを作成
    const manager = new CubismExpressionMotionManager();

    // チャンネル表情状態を作成
    const state: ChannelExpressionState = {
      expressionName: expressionName,
      motion: motion,
      managers: [manager],
      duration: duration,
      startTime: this._userTimeSeconds,
      fadingOut: false,
      fadeOutStartTime: 0.0,
      fadeOutDuration: this._defaultFadeDuration
    };

    // チャンネルに登録
    this._channelExpressions.setValue(channel, state);

    // 表情を開始
    manager.startMotionPriority(motion, false, LAppDefine.PriorityForce);

    // Sync wardrobe textures for this new expression
    this._syncWardrobeTextures();

    console.log(`Started expression '${expressionName}' on channel '${channel}' for ${duration === -1 ? 'permanent' : duration + 's'}`)
  }

  /**
   * チャンネル別表情を即座に再生する（フェードなし、初期化用）
   * @param channel チャンネル名
   * @param expressionName 表情名
   */
  public playExpressionInstant(channel: string, expressionName: string): void {
    console.log(`[playExpressionInstant START] channel='${channel}', expression='${expressionName}'`);

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]playExpressionInstant: channel=${channel}, expression=${expressionName}`);
    }

    // 表情データの取得
    const motion: ACubismMotion = this._expressions.getValue(expressionName);
    if (motion == null) {
      this._logExpressionLookupFailure('playExpressionInstant', channel, expressionName);
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]Expression '${expressionName}' not found`);
      }
      return;
    }
    console.log(`[playExpressionInstant] Motion found for '${expressionName}'`);

    // Skip if the same expression is already active and not fading out.
    if (this._channelExpressions.isExist(channel)) {
      const existing = this._channelExpressions.getValue(channel);
      if (existing != null && existing.expressionName === expressionName && !existing.fadingOut) {
        console.log(`[playExpressionInstant] Expression '${expressionName}' already active on channel '${channel}', skipping`);
        return;
      }
    }

    // 既存のチャンネル表情がある場合は停止・削除 (instant = no fade needed)
    if (this._channelExpressions.isExist(channel)) {
      const existing = this._channelExpressions.getValue(channel);
      if (existing != null) {
        console.log(`[playExpressionInstant] Stopping existing expression on channel '${channel}'`);
        this.stopChannelExpression(channel);
      }
      this._eraseChannelExpression(channel);
    }

    // 新しい表情マネージャーを作成
    const manager = new CubismExpressionMotionManager();

    // チャンネル表情状態を作成（永続）
    const state: ChannelExpressionState = {
      expressionName: expressionName,
      motion: motion,
      managers: [manager],
      duration: -1, // 永続
      startTime: this._userTimeSeconds,
      fadingOut: false,
      fadeOutStartTime: 0.0,
      fadeOutDuration: 0.0 // フェードなし
    };

    // チャンネルに登録
    this._channelExpressions.setValue(channel, state);

    // 表情を即座に開始（フェードイン時間を0に設定）
    motion.setFadeInTime(0.0);
    manager.startMotionPriority(motion, false, LAppDefine.PriorityForce);

    // Sync wardrobe textures for this new expression
    this._syncWardrobeTextures();

    console.log(`[playExpressionInstant] Applied '${expressionName}' on channel '${channel}' - Total channels: ${this._channelExpressions.getSize()}`);
  }

  /**
   * 複数の表情を1つのチャンネルに適用する（レイヤーとして重ねる）
   * @param channel チャンネル名
   * @param expressionNames 表情名の配列
   * @param isInstant trueの場合フェードなし
   */
  public playMultipleExpressions(channel: string, expressionNames: string[], isInstant: boolean = false): void {
    if (this._debugMode) {
      LAppPal.printMessage(`[APP]playMultipleExpressions: channel=${channel}, expressions=[${expressionNames.join(', ')}], instant=${isInstant}`);
    }

    // 既存のチャンネル表情がある場合はフェードアウトを開始（即削除しない）
    if (this._channelExpressions.isExist(channel)) {
      const existing = this._channelExpressions.getValue(channel);
      if (existing != null) {
        if (isInstant) {
          // Instant mode: remove immediately
          this.stopChannelExpression(channel);
          this._eraseChannelExpression(channel);
        } else {
          // Smooth mode: move old expression to a fade-out channel
          const fadeOutChannel = `__fadeout_${channel}_${Date.now()}`;
          this._eraseChannelExpression(channel);
          this._channelExpressions.setValue(fadeOutChannel, existing);
          existing.fadingOut = true;
          existing.fadeOutStartTime = this._userTimeSeconds;
          existing.fadeOutDuration = this._defaultFadeDuration;
        }
      } else {
        this._eraseChannelExpression(channel);
      }
    }

    // 各表情に独立したマネージャーを作成して同時に適用する
    const validExpressions: string[] = [];
    const managers: CubismExpressionMotionManager[] = [];
    let firstMotion: ACubismMotion | null = null;

    for (const expressionName of expressionNames) {
      const motion: ACubismMotion = this._expressions.getValue(expressionName);
      if (motion == null) {
        this._logExpressionLookupFailure('playMultipleExpressions', channel, expressionName);
        if (this._debugMode) {
          LAppPal.printMessage(`[APP]Expression '${expressionName}' not found, skipping`);
        }
        continue;
      }

      validExpressions.push(expressionName);

      if (firstMotion == null) {
        firstMotion = motion;
      }

      // フェード設定
      if (isInstant) {
        motion.setFadeInTime(0.0);
      }

      // 各表情に独立したマネージャーを作成して開始（同時適用のため）
      const mgr = new CubismExpressionMotionManager();
      mgr.startMotionPriority(motion, false, LAppDefine.PriorityForce);
      managers.push(mgr);
    }

    if (firstMotion == null || validExpressions.length === 0) {
      console.warn(`No valid expressions found for channel '${channel}'`);
      return;
    }

    // チャンネル表情状態を作成（全マネージャーを保持）
    const state: ChannelExpressionState = {
      expressionName: validExpressions.join('+'), // 複数表情を'+'で結合
      motion: firstMotion,
      managers: managers,
      duration: -1, // 永続
      startTime: this._userTimeSeconds,
      fadingOut: false,
      fadeOutStartTime: 0.0,
      fadeOutDuration: isInstant ? 0.0 : this._defaultFadeDuration
    };

    // チャンネルに登録
    this._channelExpressions.setValue(channel, state);

    // Sync wardrobe textures for the new expression set
    this._syncWardrobeTextures();

    console.log(`Applied ${validExpressions.length} layered expressions [${validExpressions.join(', ')}] on channel '${channel}' (instant: ${isInstant})`);
  }

  /**
   * 指定チャンネルの表情を停止する
   * @param channel チャンネル名
   */
  public stopExpression(channel: string): void {
    if (!this._channelExpressions.isExist(channel)) {
      if (this._debugMode) {
        LAppPal.printMessage(`[APP]No expression found on channel '${channel}'`);
      }
      return;
    }

    this.stopChannelExpression(channel);

    if (this._debugMode) {
      LAppPal.printMessage(`[APP]Stopped expression on channel '${channel}'`);
    }
  }

  /**
   * 現在のチャンネル表情名を取得する
   * @param channel チャンネル名
   * @returns 表情名、なければnull
   */
  public getCurrentExpression(channel: string): string | null {
    if (!this._channelExpressions.isExist(channel)) {
      return null;
    }

    const state = this._channelExpressions.getValue(channel);
    if (state == null) return null;
    return state.fadingOut ? null : state.expressionName;
  }

  /**
   * すべてのアクティブな表情を取得する
   * @returns チャンネル名をキー、表情名を値とするオブジェクト
   */
  public getAllActiveExpressions(): { [channel: string]: string } {
    const result: { [channel: string]: string } = {};

    for (let i = 0; i < this._channelExpressions.getSize(); i++) {
      const channel = this._channelExpressions._keyValues[i].first;
      const state = this._channelExpressions._keyValues[i].second;

      if (state != null && !state.fadingOut) {
        result[channel] = state.expressionName;
      }
    }

    return result;
  }

  /**
   * 指定チャンネルをクリアする
   * @param channel チャンネル名
   */
  public clearChannel(channel: string): void {
    this.stopExpression(channel);
  }

  /**
   * すべてのチャンネルをクリアする
   */
  public clearAllExpressions(): void {
    const channels: string[] = [];

    // チャンネル名を収集
    for (let i = 0; i < this._channelExpressions.getSize(); i++) {
      channels.push(this._channelExpressions._keyValues[i].first);
    }

    // 各チャンネルを停止
    for (const channel of channels) {
      this.stopChannelExpression(channel);
    }

    // Unload all manifest-tracked textures since no expressions are active
    if (channels.length > 0) {
      this._syncWardrobeTextures();
    }

    if (this._debugMode) {
      LAppPal.printMessage('[APP]Cleared all channel expressions');
    }
  }

  /**
   * 指定チャンネルの残り時間を取得する
   * @param channel チャンネル名
   * @returns 残り時間（秒）、永続の場合は-1、チャンネルがない場合は0
   */
  public getChannelDuration(channel: string): number {
    if (!this._channelExpressions.isExist(channel)) {
      return 0;
    }

    const state = this._channelExpressions.getValue(channel);
    if (state == null) return 0;
    if (state.duration === -1) {
      return -1; // 永続
    }

    const elapsed = this._userTimeSeconds - state.startTime;
    const remaining = state.duration - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * チャンネル表情システムの更新処理
   * @param deltaTimeSeconds デルタ時間
   */
  private updateChannelExpressions(deltaTimeSeconds: number): void {
    const channelsToRemove: string[] = [];

    // チャンネルの処理を行う（逆順で処理してインデックスの問題を回避）
    for (let i = this._channelExpressions.getSize() - 1; i >= 0; i--) {
      const channel = this._channelExpressions._keyValues[i].first;
      const state = this._channelExpressions._keyValues[i].second;

      // Null check for safety
      if (state == null) {
        channelsToRemove.push(channel);
        continue;
      }

      // フェードアウト中の処理
      if (state.fadingOut) {
        const fadeElapsed = this._userTimeSeconds - state.fadeOutStartTime;
        if (fadeElapsed >= state.fadeOutDuration) {
          // フェードアウト完了、チャンネルを削除
          channelsToRemove.push(channel);
          continue;
        }

        // フェードアウト中の表情更新
        const fadeProgress = fadeElapsed / state.fadeOutDuration;
        const fadeWeight = 1.0 - fadeProgress; // 1.0から0.0へ
        for (const mgr of state.managers) {
          mgr.updateMotion(this._model, deltaTimeSeconds);
        }
        continue;
      }

      // 通常の表情更新（全マネージャーを更新して全表情を同時適用）
      for (const mgr of state.managers) {
        mgr.updateMotion(this._model, deltaTimeSeconds);
      }

      // 時間制限のある表情の期限チェック
      if (state.duration !== -1) {
        const elapsed = this._userTimeSeconds - state.startTime;
        if (elapsed >= state.duration) {
          // 期限切れ、フェードアウト開始
          state.fadingOut = true;
          state.fadeOutStartTime = this._userTimeSeconds;
          console.log(`Expression '${state.expressionName}' on channel '${channel}' expired, starting fade out`);
        }
      }
    }

    // 削除対象のチャンネルをクリーンアップ
    for (const channel of channelsToRemove) {
      this._eraseChannelExpression(channel);
      console.log(`Removed expired expression from channel '${channel}'`);
    }

    // If any channel was removed, re-sync textures (may unload no-longer-needed textures)
    if (channelsToRemove.length > 0) {
      this._syncWardrobeTextures();
    }
  }  /**
   * チャンネル表情の内部停止処理
   * @param channel チャンネル名
   */
  private stopChannelExpression(channel: string): void {
    if (!this._channelExpressions.isExist(channel)) {
      return;
    }

    const state = this._channelExpressions.getValue(channel);

    // Guard against null entries left by previous setValue(key, null)
    if (state == null) {
      this._eraseChannelExpression(channel);
      return;
    }

    // フェードアウト開始
    if (!state.fadingOut) {
      state.fadingOut = true;
      state.fadeOutStartTime = this._userTimeSeconds;
      console.log(`Starting fade out for expression '${state.expressionName}' on channel '${channel}'`);
    }
  }

  /**
   * Properly remove a channel entry from the csmMap (splice-based erasure).
   */
  private _eraseChannelExpression(channel: string): void {
    for (let i = 0; i < this._channelExpressions.getSize(); i++) {
      if (this._channelExpressions._keyValues[i].first === channel) {
        this._channelExpressions._keyValues.splice(i, 1);
        (this._channelExpressions as any)._size--;
        return;
      }
    }
  }

  /**
   * コンストラクタ
   */
  public constructor() {
    super();

    this._modelSetting = null;
    this._modelHomeDir = null;
    this._userTimeSeconds = 0.0;

    this._eyeBlinkIds = new csmVector<CubismIdHandle>();
    this._lipSyncIds = new csmVector<CubismIdHandle>();

    this._motions = new csmMap<string, ACubismMotion>();
    this._expressions = new csmMap<string, ACubismMotion>();

    this._hitArea = new csmVector<csmRect>();
    this._userArea = new csmVector<csmRect>();

    this._idParamAngleX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleX
    );
    this._idParamAngleY = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleY
    );
    this._idParamAngleZ = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamAngleZ
    );
    this._idParamEyeBallX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamEyeBallX
    );
    this._idParamEyeBallY = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamEyeBallY
    );
    this._idParamBodyAngleX = CubismFramework.getIdManager().getId(
      CubismDefaultParameterId.ParamBodyAngleX
    );

    if (LAppDefine.MOCConsistencyValidationEnable) {
      this._mocConsistency = true;
    }

    this._state = LoadStep.LoadAssets;
    this._expressionCount = 0;
    this._textureCount = 0;
    this._motionCount = 0;
    this._allMotionCount = 0;
    this._wavFileHandler = new LAppWavFileHandler();
    this._consistency = false;

    // Initialize talking animation variables
    this._isTalking = false;
    this._isClosingMouth = false;
    this._mouthCloseTime = 0.0;
    this._talkingTime = 0.0;
    this._talkingSpeed = 4.0; // Slower default speed (oscillations per second)
    this._talkingIntensity = 0.6; // More subtle default intensity
    this._originalLipSyncState = false;
    this._currentVowel = 'A';
    this._vowelChangeTime = 0.0;
    this._vowelDuration = 0.2; // Change vowel every 0.2 seconds

    // Initialize channel expression system
    this._channelExpressions = new csmMap<string, ChannelExpressionState>();
    this._defaultFadeDuration = 0.3; // Default fade transition duration

    this._loadedExpressionNames = new Set<string>();
    this._devDefaultsApplied = false;
    this._failedExpressionNames = new Map<string, ExpressionLoadFailure>();
    this._expressionFileMap = new Map<string, string>();
    this._manifestExpressionPaths = new Map<string, string>();
    this._expressionLoadPromises = new Map<string, Promise<boolean>>();
    this._priorityExpressionNames = new Set<string>();
    this._priorityExpressionsLoaded = false;
    this._loadStartTime = 0;
    this._priorityLoadStartTime = 0;
    this._baseModelMatrixArray = null;

    // Initialize wardrobe texture management
    this._textureIndexMap = new Map();
    this._requiredWardrobeTextures = new Set();
    this._wardrobeOwnedTextures = new Set();
    this._transparentTexture = null;
    this._textureSwapRunning = false;
    this._queuedTextureSwap = null;
    this._textureSwapGeneration = 0;
    this._textureSwapSafetyTimer = 0;
    this._wardrobeRenderBlocked = false;

    // Initialize expression manifest system
    this._expressionTextures = new Map();
    this._expressionChannelMap = new Map();
    this._allManifestTextures = new Set();
    this._channelManifestTextures = new Map();
    this._manifestLoaded = false;
    this._pendingTextureSync = false;
    this._modelYOffset = LAppDefine.ModelInitialYOffset;
    this._modelYOffsetCurrent = this._modelYOffset;
    this._modelYOffsetStart = this._modelYOffset;
    this._modelYOffsetTarget = this._modelYOffset;
    this._modelYOffsetElapsed = 0;
    this._modelYOffsetDuration = 0;
    this._modelYOffsetAnimating = false;
    this._modelYOffsetDirty = false;
    this._modelXOffset = 0;
    this._modelXOffsetCurrent = 0;
    this._modelXOffsetStart = 0;
    this._modelXOffsetTarget = 0;
    this._modelXOffsetElapsed = 0;
    this._modelXOffsetDuration = 0;
    this._modelXOffsetAnimating = false;
    this._modelXOffsetDirty = false;
    const neutralLight = { r: 1, g: 1, b: 1, a: 1 };
    this._lightColorTarget = { ...neutralLight };
    this._lightColorCurrent = { ...neutralLight };
    this._lightColorStart = { ...neutralLight };
    this._lightIntensityTarget = 0;
    this._lightIntensityCurrent = 0;
    this._lightIntensityStart = 0;
    this._lightTransitionElapsed = 0;
    this._lightTransitionDuration = 0;
    this._lightAnimating = false;
    this._lightLastRequestedMs = 0;
    this._lightColorDirty = true;
    this._lastRendererColor = null;
  }

  public setModelYOffset(offset: number, durationMs = 0): void {
    this._modelYOffset = offset;
    const durationSeconds = Math.max(0, durationMs) / 1000;

    if (!this._baseModelMatrixArray) {
      this._modelYOffsetCurrent = offset;
      this._modelYOffsetTarget = offset;
      this._modelYOffsetAnimating = false;
      this._modelYOffsetDirty = true;
      return;
    }

    if (durationSeconds <= 0) {
      this._modelYOffsetCurrent = offset;
      this._modelYOffsetTarget = offset;
      this._modelYOffsetAnimating = false;
      this._modelYOffsetDirty = true;
      this._applyModelOffsets();
      return;
    }

    this._modelYOffsetStart = this._modelYOffsetCurrent;
    this._modelYOffsetTarget = offset;
    this._modelYOffsetElapsed = 0;
    this._modelYOffsetDuration = durationSeconds;
    this._modelYOffsetAnimating = true;
  }

  public setModelXOffset(offset: number, durationMs = 0): void {
    this._modelXOffset = offset;
    const durationSeconds = Math.max(0, durationMs) / 1000;

    if (!this._baseModelMatrixArray) {
      this._modelXOffsetCurrent = offset;
      this._modelXOffsetTarget = offset;
      this._modelXOffsetAnimating = false;
      this._modelXOffsetDirty = true;
      return;
    }

    if (durationSeconds <= 0) {
      this._modelXOffsetCurrent = offset;
      this._modelXOffsetTarget = offset;
      this._modelXOffsetAnimating = false;
      this._modelXOffsetDirty = true;
      this._applyModelOffsets();
      return;
    }

    this._modelXOffsetStart = this._modelXOffsetCurrent;
    this._modelXOffsetTarget = offset;
    this._modelXOffsetElapsed = 0;
    this._modelXOffsetDuration = durationSeconds;
    this._modelXOffsetAnimating = true;
  }

  public getModelYOffset(): number {
    return this._modelYOffset;
  }

  public getModelXOffset(): number {
    return this._modelXOffset;
  }

  private _captureBaseModelMatrix(): void {
    if (!this._modelMatrix) {
      return;
    }

    const current = this._modelMatrix.getArray();
    this._baseModelMatrixArray = new Float32Array(current.length);
    this._baseModelMatrixArray.set(current);
    this._modelYOffsetCurrent = this._modelYOffset;
    this._modelYOffsetStart = this._modelYOffsetCurrent;
    this._modelYOffsetTarget = this._modelYOffsetCurrent;
    this._modelYOffsetElapsed = 0;
    this._modelYOffsetDuration = 0;
    this._modelYOffsetAnimating = false;
    this._modelYOffsetDirty = true;
    this._modelXOffsetCurrent = this._modelXOffset;
    this._modelXOffsetStart = this._modelXOffsetCurrent;
    this._modelXOffsetTarget = this._modelXOffsetCurrent;
    this._modelXOffsetElapsed = 0;
    this._modelXOffsetDuration = 0;
    this._modelXOffsetAnimating = false;
    this._modelXOffsetDirty = true;
    this._applyModelOffsets();
  }

  private _applyModelOffsets(): void {
    if (!this._baseModelMatrixArray || !this._modelMatrix) {
      return;
    }

    const adjusted = new Float32Array(this._baseModelMatrixArray.length);
    adjusted.set(this._baseModelMatrixArray);
    adjusted[12] += this._modelXOffsetCurrent;
    adjusted[13] += this._modelYOffsetCurrent;
    this._modelMatrix.setMatrix(adjusted);
    this._modelYOffsetDirty = false;
    this._modelXOffsetDirty = false;
  }

  private _updateModelYOffset(deltaTimeSeconds: number): void {
    if (!this._baseModelMatrixArray) {
      return;
    }

    if (this._modelYOffsetAnimating) {
      this._modelYOffsetElapsed += deltaTimeSeconds;
      const duration = this._modelYOffsetDuration > 0 ? this._modelYOffsetDuration : deltaTimeSeconds;
      let progress = duration > 0 ? this._modelYOffsetElapsed / duration : 1;
      if (progress >= 1) {
        progress = 1;
        this._modelYOffsetAnimating = false;
      }
      const eased = this._easeInOutCubic(progress);
      this._modelYOffsetCurrent =
        this._modelYOffsetStart +
        (this._modelYOffsetTarget - this._modelYOffsetStart) * eased;
      this._applyModelOffsets();
    } else if (this._modelYOffsetDirty) {
      this._modelYOffsetCurrent = this._modelYOffset;
      this._applyModelOffsets();
    }
  }

  private _updateModelXOffset(deltaTimeSeconds: number): void {
    if (!this._baseModelMatrixArray) {
      return;
    }

    if (this._modelXOffsetAnimating) {
      this._modelXOffsetElapsed += deltaTimeSeconds;
      const duration = this._modelXOffsetDuration > 0 ? this._modelXOffsetDuration : deltaTimeSeconds;
      let progress = duration > 0 ? this._modelXOffsetElapsed / duration : 1;
      if (progress >= 1) {
        progress = 1;
        this._modelXOffsetAnimating = false;
      }
      const eased = this._easeInOutCubic(progress);
      this._modelXOffsetCurrent =
        this._modelXOffsetStart +
        (this._modelXOffsetTarget - this._modelXOffsetStart) * eased;
      this._applyModelOffsets();
    } else if (this._modelXOffsetDirty) {
      this._modelXOffsetCurrent = this._modelXOffset;
      this._applyModelOffsets();
    }
  }

  private _updateLightColor(deltaTimeSeconds: number): void {
    if (!this._lightAnimating) {
      return;
    }

    if (this._lightTransitionDuration <= 0) {
      this._lightColorCurrent = { ...this._lightColorTarget };
      this._lightIntensityCurrent = this._lightIntensityTarget;
      this._lightAnimating = false;
      this._lightColorDirty = true;
      return;
    }

    this._lightTransitionElapsed += deltaTimeSeconds;
    let progress = this._lightTransitionElapsed / this._lightTransitionDuration;
    if (progress >= 1) {
      progress = 1;
      this._lightAnimating = false;
    }

    const eased = this._easeInOutCubic(progress);

    const startColor = this._lightColorStart ?? this._lightColorCurrent;
    this._lightColorCurrent = {
      r: startColor.r + (this._lightColorTarget.r - startColor.r) * eased,
      g: startColor.g + (this._lightColorTarget.g - startColor.g) * eased,
      b: startColor.b + (this._lightColorTarget.b - startColor.b) * eased,
      a: startColor.a + (this._lightColorTarget.a - startColor.a) * eased
    };

    const startIntensity =
      this._lightIntensityStart ?? this._lightIntensityCurrent;
    this._lightIntensityCurrent =
      startIntensity +
      (this._lightIntensityTarget - startIntensity) * eased;

    this._lightColorDirty = true;

    if (!this._lightAnimating) {
      this._lightColorCurrent = { ...this._lightColorTarget };
      this._lightIntensityCurrent = this._lightIntensityTarget;
      this._lightColorStart = { ...this._lightColorCurrent };
      this._lightIntensityStart = this._lightIntensityCurrent;
      this._lightTransitionElapsed = 0;
      this._lightTransitionDuration = 0;
    }
  }

  private _easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  _modelSetting: ICubismModelSetting; // モデルセッティング情報
  _modelHomeDir: string; // モデルセッティングが置かれたディレクトリ
  _userTimeSeconds: number; // デルタ時間の積算値[秒]

  _eyeBlinkIds: csmVector<CubismIdHandle>; // モデルに設定された瞬き機能用パラメータID
  _lipSyncIds: csmVector<CubismIdHandle>; // モデルに設定されたリップシンク機能用パラメータID

  _motions: csmMap<string, ACubismMotion>; // 読み込まれているモーションのリスト
  _expressions: csmMap<string, ACubismMotion>; // 読み込まれている表情のリスト

  _hitArea: csmVector<csmRect>;
  _userArea: csmVector<csmRect>;

  _idParamAngleX: CubismIdHandle; // パラメータID: ParamAngleX
  _idParamAngleY: CubismIdHandle; // パラメータID: ParamAngleY
  _idParamAngleZ: CubismIdHandle; // パラメータID: ParamAngleZ
  _idParamEyeBallX: CubismIdHandle; // パラメータID: ParamEyeBallX
  _idParamEyeBallY: CubismIdHandle; // パラメータID: ParamEyeBAllY
  _idParamBodyAngleX: CubismIdHandle; // パラメータID: ParamBodyAngleX

  _state: LoadStep; // 現在のステータス管理用
  _expressionCount: number; // 表情データカウント
  _textureCount: number; // テクスチャカウント
  _motionCount: number; // モーションデータカウント
  _allMotionCount: number; // モーション総数
  _wavFileHandler: LAppWavFileHandler; //wavファイルハンドラ
  _consistency: boolean; // MOC3一貫性チェック管理用

  // Talking animation variables
  private _isTalking: boolean; // 話している状態かどうか
  private _isClosingMouth: boolean; // 口を閉じるアニメーション中かどうか
  private _mouthCloseTime: number; // 口を閉じるアニメーションの経過時間
  private _talkingTime: number; // 話し始めてからの経過時間
  private _talkingSpeed: number; // 話す速度（口の開閉速度）
  private _talkingIntensity: number; // 話す強度（口の開閉幅）
  private _originalLipSyncState: boolean; // 元のリップシンク状態を保存
  private _currentVowel: string; // 現在の母音形状 (A, E, I, O, U)
  private _vowelChangeTime: number; // 次の母音変更までの時間
  private _vowelDuration: number; // 各母音を保持する時間

  // Channel expression system variables
  private _channelExpressions: csmMap<string, ChannelExpressionState>; // チャンネル別表情管理
  private _defaultFadeDuration: number; // デフォルトフェード時間

  // Wardrobe texture management
  private _textureIndexMap: Map<string, { index: number; fullPath: string }>; // basename → GPU slot
  private _requiredWardrobeTextures: Set<string>; // basenames currently required by equipped items
  private _wardrobeOwnedTextures: Set<string>;    // basenames that belong to any wardrobe item
  private _transparentTexture: WebGLTexture | null; // 1×1 transparent placeholder bound to unloaded slots
  private _textureSwapRunning: boolean;            // true while a swap is in-flight
  private _queuedTextureSwap: { required: Set<string>; owned: Set<string> } | null; // latest-wins queue (at most 1)
  private _textureSwapGeneration: number;          // monotonic counter — stale callbacks detect superseded swaps
  private _textureSwapSafetyTimer: number;         // setTimeout id for the global safety timeout
  private _wardrobeRenderBlocked: boolean;         // true when model was loaded with pending wardrobe data — blocks draw() until wardrobe textures confirmed

  // Expression manifest system (folder-based auto-loading)
  private _expressionTextures: Map<string, Set<string>>; // expression name → required texture basenames
  private _expressionChannelMap: Map<string, string>;    // expression name → wardrobe channel (from manifest/wardrobe config)
  private _allManifestTextures: Set<string>;             // all textures tracked by any manifest folder
  private _channelManifestTextures: Map<string, Set<string>>; // wardrobe channel → textures owned by that channel
  private _manifestLoaded: boolean;                     // whether manifest fetch has completed
  private _pendingTextureSync: boolean;                  // true when a sync is deferred until ready

  private _loadedExpressionNames: Set<string>;
  private _devDefaultsApplied: boolean;
  private _failedExpressionNames: Map<string, ExpressionLoadFailure>;
  private _expressionFileMap: Map<string, string>;
  private _manifestExpressionPaths: Map<string, string>;
  private _expressionLoadPromises: Map<string, Promise<boolean>>;
  private _priorityExpressionNames: Set<string>; // Wardrobe expressions to load first
  private _priorityExpressionsLoaded: boolean; // Flag for priority phase completion
  private _loadStartTime: number; // Timestamp when model loading started
  private _priorityLoadStartTime: number; // Timestamp when priority expression loading started
  private _baseModelMatrixArray: Float32Array | null;
  private _modelYOffset: number;
  private _modelYOffsetCurrent: number;
  private _modelYOffsetStart: number;
  private _modelYOffsetTarget: number;
  private _modelYOffsetElapsed: number;
  private _modelYOffsetDuration: number;
  private _modelYOffsetAnimating: boolean;
  private _modelYOffsetDirty: boolean;
  private _modelXOffset: number;
  private _modelXOffsetCurrent: number;
  private _modelXOffsetStart: number;
  private _modelXOffsetTarget: number;
  private _modelXOffsetElapsed: number;
  private _modelXOffsetDuration: number;
  private _modelXOffsetAnimating: boolean;
  private _modelXOffsetDirty: boolean;
  private _lightColorTarget: { r: number; g: number; b: number; a: number };
  private _lightColorCurrent: { r: number; g: number; b: number; a: number };
  private _lightColorStart: { r: number; g: number; b: number; a: number };
  private _lightIntensityTarget: number;
  private _lightIntensityCurrent: number;
  private _lightIntensityStart: number;
  private _lightTransitionElapsed: number;
  private _lightTransitionDuration: number;
  private _lightAnimating: boolean;
  private _lightLastRequestedMs: number;
  private _lightColorDirty: boolean;
  private _lastRendererColor: { r: number; g: number; b: number; a: number } | null;
}
